/**
 * The nursing-home request inbox, inside the kiosk (T788 M3).
 *
 * WHAT THIS REPLACES. Helen used to leave the kiosk, open the portal's /ops
 * page in a browser and type a shared passcode to see these. She now reads and
 * prices them here, signed in as herself, and the passcode never reaches her
 * browser at all - the kiosk server holds it and calls the portal.
 *
 * ⛔ THIS IS A RE-DRESS, NOT A PORT. The portal screen's markup was NOT copied.
 * The two stylesheets share exactly five class names - `.btn`, `.btn--primary`,
 * `.btn--secondary`, `.field-hint`, `.field-label` - and some of the five carry
 * different declarations on each side. ⚠️ `.field-hint` IS NO LONGER ONE OF THEM:
 * it read "grey in the portal and GOLD here" until T1749 M7, when Q139 (Alex,
 * 2026-09-11) ruled ONE field-hint colour, `--ink-600` on both tools, and this
 * sheet moved. `.field-label` is still a block with different spacing. The fifth,
 * `.btn--secondary`, is the trap: its base rule is byte-identical, so a diff
 * says "same", while the portal's `.ops-table .btn--secondary` - which is what
 * holds those buttons at 44px and caption size - has no kiosk counterpart at
 * all. So a grep- or diff-based "did the styling survive" check passes wrongly
 * in both directions here. It has to be looked at.
 *
 * ⛔ NO PASSCODE PROMPT ANYWHERE. `Ops.tsx`'s passcode / authedPasscode /
 * rePrompt state machine is deliberately NOT ported: authentication is the
 * kiosk session Helen already has. A 503 renders a banner saying the link is
 * not configured - never a credential prompt, because there is no credential
 * for her to type and asking would teach her to expect one.
 *
 * ⭐ SELF-FETCHING, like `Operators`. Nothing is appended to `Admin.tsx`'s
 * seven-entry `Promise.all`, and Admin gates this on `view.kind` ALONE. Both
 * are the same decision: this screen reads none of rates/queue/meta, so it must
 * stay reachable during a rates outage - and the way back out is on it.
 *
 * ═════════════════════════════════════════════════════════════════════════
 * ⛔⛔ THERE IS NO HOME QUESTION ON THIS SCREEN (Alex, 2026-09-04 on #1745;
 * T1746 M5 B7, DD8) - AND ITS ABSENCE IS THE FEATURE, NOT AN OMISSION.
 * ═════════════════════════════════════════════════════════════════════════
 *
 * Every request already belongs to a home: the nurse who raised it works at
 * one. Until M5 this screen asked Helen anyway, because the seam projection
 * drops the request's `homeId` and nothing in the browser could derive it - so
 * her pick WAS the account resolution, and #1720 S4 recorded what that costs:
 * any home's key could be posted against any request and be frozen onto the
 * booking's `account_id`, permanently, with nothing on any screen saying so.
 * ⭐ THE CROSSING NOW DERIVES THE HOME SERVER-SIDE from the request's own key
 * and refuses a body `homeId` that disagrees, and the agreed price is asked for
 * by REQUEST ID alone. NOBODY CAN PICK THE WRONG HOME IF NOBODY PICKS. The
 * request's home key still never reaches this browser (S12); what reaches it is
 * the home's NAME, on the row, which is the thing Helen reads.
 * ⛔ DO NOT RESTORE A PICKER "so she can correct it". Correcting which home a
 * request came from is a PORTAL-side act on the request, and a pick here would
 * be refused by the crossing as a mismatch - a control whose only outcome is a
 * 409 is worse than no control.
 *
 * ⚠️ RETIRED WITH THE PICKER: THE FREE-TEXT DESTINATION PATH. A legacy row that
 * carries no kiosk destination key used to get a second `<select>` of the
 * account's priced destinations, so Helen could choose what to price. That
 * picker needed an `accountId`, which only the home pick could supply; the
 * agreed-price response deliberately carries no account key (an offer must not
 * hand a browser the account behind a home), so there is nothing left to feed
 * it. Such a row now gets the `no-destination` line and Helen types the figure,
 * exactly as she did before T1683 M1. ⇒ TRIGGER TO REVISIT: if the offer route
 * ever answers WHICH destinations the request's home is priced to, the picker
 * can come back on that answer alone - never on a home question.
 */
import { Fragment, type ComponentChildren, type VNode } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import {
  listHomeRequests,
  listPortalRequests,
  convertPortalRequestToBooking,
  setPortalRequestStatus,
  markPortalRequestSeen,
  cancelBookingRun,
  agreedPriceFor,
  acceptPortalAsk,
  declinePortalAsk,
  bookingRequestMismatch,
  mismatchSteer,
  // ⭐ Board 2814 M2 (DD-11): the one table, and the ask's words shared with booking detail.
  needsHelenNow,
  canEndRunHere,
  askDeclineQuestion,
  askLineOf,
  askNotice,
  historyLinesOf,
  portalRunNotice,
  DECLINE_REASON_WORDS,
  CROSSINGS_UNKNOWN_LINE,
  // ⭐ Board 2814 M4: hand settlement (DD-11 rows 7, 8, 14) and the travelled words.
  settlePortalRequest,
  undoPortalRequestSettlement,
  SETTLE_REASON_WORDS,
  runTravelledText,
  // ⭐ Board 2814 M8R1 (Q287): a travelled run's request stays open.
  TRAVELLED_REQUEST_COPY,
  type PortalSettlement,
  type HelenPlace,
  maybeDuplicateText,
  type CrossingDuplicate,
  type PortalCrossingOutcome,
  type PortalRequest,
  type PortalCrossing,
  type PortalRequestBookingOutcome,
  type AgreedPrice,
  // ⭐ T1868 M6 — the home shape only; this screen makes no home CALL, and must
  // not name one even in prose (see `homes` on the props type).
  type PortalHome,
} from '../../lib/actions.js';
import { bookingStatusLabel, type Registry } from '../../store/registry.js';
import { Banner } from '../../components/Banner.js';
import { ApiError } from '../../lib/api.js';
import { PAYER_WORDS } from '../../lib/payer-words.js';
// ⭐⭐ T1749 M7 (R4) — THE TEN-COLUMN TABLE IS A `Card` ROW LIST NOW, so this
// screen composes the library rather than drawing a table of its own. `Card` is
// the row/card box, `Chip` the price-state pill, `FactsTable` the ruled
// label-and-value block (DECISIONS.md §13, from D2) and `Button` the action
// grammar for the confirm limbs.
import {
  ActionRow,
  Button,
  Chip,
  EmptyState,
  FormField,
  FactsTable,
  List,
  Row,
} from '@ecmb/ui';
import { BANDS } from '../../../engine/bands.js';
// ⭐ T1717 M6 - the ONE Dublin instant rendering, shared with BookingDetail,
// CustomerInfoBlock and the driver/team message. See its header for why the
// four surfaces do not each carry their own Intl call.
import {
  RETURN_TIME_TO_CONFIRM,
  dublinCivilDate,
  formatDublinInstant,
  formatReturnWhen,
} from '../../../shared/dublin-date.js';
// ⭐ Board 2647 (K2) - the ONE lead rule, shared with the crossing (`crossingLead`).
// ⭐ Board 2814 M2 - and the ONE request day the crossing books on (`portalRequestDay`).
import { portalRequestLead, portalRequestDay } from '../../../shared/portal-lead.js';
import { isBookingCancellable, travelledRunKeepsRequest } from '../../../shared/status-schema.js';
import { requestActionChip } from '../../../shared/booking-chip-words.js';
/* ⭐ board 2812 M3 (M2 carry 5): THE ROW'S OWN CHEVRON IS GONE. The card head is the
   shared `Row`, which draws the one cue itself; passing `ChevronIcon` too would draw
   two (`portal-requests.spec.tsx` pins one cue per card head). */

export type PortalRequestsProps = {
  /**
   * ⛔ CALLED AFTER EVERY SUCCESSFUL STATUS WRITE, and it is REQUIRED rather
   * than optional so a future mount cannot silently forget it.
   *
   * The rail's nursing-home badge counts the requests in Needs you now
   * (`needsHelenNow`, board 2814 M2, DD-11), and this screen is where that count
   * CHANGES. Without
   * this the badge only moved on its 30s poll, so pricing a request left the
   * row reading "Priced" while the badge beside it still counted it — the same
   * screen contradicting itself, immediately after Helen's own action. That is
   * the defect shape this repo already pins for the sibling web-enquiries badge
   * (the bug-f regression guard), and `Inbox` avoids it the same way: its
   * `onSetStatus` refreshes the count in App rather than only reloading the
   * list.
   *
   * The local `load()` stays — it refreshes the ROWS. This refreshes the COUNT.
   * They are two different reads and both have to happen.
   */
  onChanged: () => void;
  /**
   * ⭐ Board 2814 M8R1 (R3) - App's portal-count poll tick (`App.tsx`, the ONE
   * 30-second timer). Each new value reloads the ROWS quietly, so the list follows
   * the count. Optional: absent, the rows load on mount and after Helen's own acts.
   */
  pollTick?: number;
  /**
   * ⭐⭐ T1746 M1 (B4, Q108 option b) - OPEN THE BOOKING THIS REQUEST MADE.
   *
   * ⛔ REQUIRED, NOT OPTIONAL, for the reason `onChanged` above is: a crossed
   * row renders a control that says it opens a booking, and a mount that forgot
   * to wire it would render a button that does nothing while still claiming to.
   *
   * ⛔ IT IS THE SAME NAVIGATION `Bookings` USES (`screens/Bookings.tsx`, its
   * `onOpen`), threaded from App rather than re-derived here: this screen holds
   * no booking state and must not learn any. Q108 asked whether the kiosk should
   * close the two-cancel loop; option (b) is "show the linked booking on the
   * request row so the second cancel is one click away", which is a NAVIGATION,
   * never a write to the other rail.
   */
  onOpenBooking: (bookingId: number) => void;
  /**
   * ⛔ THE STATUS WORD IS THE REGISTRY'S, NEVER THIS FILE'S. `bookingStatusLabel`
   * is the one place a `bookings.status` value becomes an operator word, and the
   * crossed row renders that word beside the reference. A local map here would
   * be a second vocabulary for the same column, drifting the moment the registry
   * gains a state.
   */
  registry: Registry;
  /**
   * ⭐⭐ T1868 M6 (P0-2, P1-2) — THE HOMES, SO HELEN HAS A NUMBER TO RING.
   *
   * ⛔ A PROP, NEVER A FETCH FROM THIS SCREEN, and that is a control rather than
   * a convenience. `test/core/portal-home-read-census.spec.ts` is BIDIRECTIONAL
   * over the home-reader names, and this file's `LoadState` note records that it
   * reads no home list at all. A fetch here would need a declared role in that
   * census; a prop needs none, because it IS the homes tab's own already-loaded
   * array handed down. ⚠️ For the same reason nothing in this file may name
   * either home reader IN A COMMENT — the census matches source text, and it
   * says so deliberately.
   *
   * ⛔ AND IT WIDENS NO ALLOWLIST. The alternative was adding a key to
   * `PORTAL_REQUEST_KEEP_KEYS` so every request carried its home's number; this
   * adds NO per-request egress and leaves that allowlist exactly as it was.
   *
   * ⚠️ IT IS `[]` UNTIL THE HOMES TAB'S OWN LOAD RESOLVES, which on the default
   * `requests` tab means the first paint. `fallbackPhoneFor` answers null for an
   * empty array, so the line is simply absent — it never renders a claim it
   * cannot support.
   */
  homes: PortalHome[];
  /**
   * ⭐⭐ board 2812 M3 (R-2812-f) — HOSTED IN NURSING HOMES' TO DO, ONE CARD OPEN ON
   * THE WHOLE SCREEN. The To do opens another pile's card and asks this list to
   * close its own (`suppressOpen`); a row Helen opens here tells the To do to close
   * its card (`onOpenRow`). Absent on any other mount: the list owns its own open row.
   */
  suppressOpen?: boolean;
  onOpenRow?: () => void;
  /** ⭐ board 2812 M3: the row to open first (Tomorrow's waiting row opens its card
   *  here); read once, at mount. By request id, which this route serves by design. */
  openRequestId?: number;
  /** ⭐ board 2812 M3: hosted in a To do, whose own pile heading ("N Need you now")
   *  names this list, so the list draws no "Needs you now" heading of its own. */
  inTodo?: boolean;
  /**
   * ⭐ board 2812 M3 fix round 2 (UX-8 / PA-6): the host places the two lists. The To do
   * puts Need you now first and the quiet Done list LAST, after its own piles, so it
   * hands this a layout: `needs` is everything this screen draws above the Done list
   * (its notices and Need you now), `done` the Done list or null. Absent on any other
   * mount: the Done list follows Need you now, as before.
   */
  arrange?: (needs: ComponentChildren, done: ComponentChildren) => ComponentChildren;
  /**
   * ⭐ board 2812 M4 (plan step 4): ONE HOME'S REQUESTS, by its group key (Q240), read
   * through M1's home filter on the server; the rows drop the home's name, which the
   * home's page names once. Absent: every home's requests, as before.
   */
  homeKey?: string;
  /** ⭐ board 2812 M4 fix round (C2): with `homeKey`, the home is unknown to the server
   *  (404), so the host falls back to All homes (`TodoPiles`' `onStale`, the same rule). */
  onStale?: () => void;
  /** ⭐ board 2812 M4: the homes column sits beside the list (the Row's layout). */
  beside?: boolean;
};

/**
 * ⭐⭐ THE HOME'S OWN NUMBER FOR ONE REQUEST ROW — T1868 M6, and it is the
 * replacement for the next-of-kin number that used to reach Helen.
 *
 * ⛔⛔ RESOLVED BY NAME, BECAUSE THE ROW CARRIES NO ID, AND `portal.homes.name`
 * IS **NOT UNIQUE** — `TEXT NOT NULL` with no unique index (migration v1),
 * verified rather than assumed. So an ambiguous name answers NULL and the line
 * is absent. ⛔ SHOWING THE FIRST MATCH WOULD BE THE DEFECT: two homes sharing a
 * name would put one home's switchboard on the other's job, and Helen would ring
 * a stranger about an identified resident's journey. An absent line costs her one
 * lookup on the Homes tab; a wrong line costs a phone call she cannot take back.
 *
 * ⚠️ AN EMPTY ARRAY, AN UNKNOWN NAME, AN AMBIGUOUS NAME AND A HOME WITH NO
 * NUMBER ALL ANSWER NULL, deliberately: every one of them means "this screen
 * cannot say", and distinguishing them on the row would be four states for a
 * value that is either dialable or not.
 *
 * ⚠️ TRIMMED ON BOTH SIDES BUT NOT CASE-FOLDED OR CLEANED. `homeName` and
 * `name` are the SAME COLUMN read twice, so any normalisation here would be a
 * second rule to keep in step with the portal's own; trimming is the one
 * difference the wire can introduce.
 */
export function fallbackPhoneFor(homes: PortalHome[], homeName: string): string | null {
  // ⛔ NEVER THROW ON A LIST ROW (T1749 M7; the same rule `Card.tsx`'s step line
  // and `format-when.ts` both state). This reads a value off the ops seam, and a
  // row that arrives without a home name at all — an older portal, a degraded
  // projection — must cost itself its phone line and not the whole screen. Found
  // by a real run: a fixture row with no `homeName` threw here and blanked every
  // other row with it, which is the exact class the malformed-row arm guards.
  const wanted = typeof homeName === 'string' ? homeName.trim() : '';
  if (wanted === '') return null;
  const matches = homes.filter((h) => h.name.trim() === wanted);
  if (matches.length !== 1) return null;
  const phone = matches[0]!.fallback_phone;
  return typeof phone === 'string' && phone.trim() !== '' ? phone : null;
}

/** One id, because one amount error is shown at a time (RateList's
 *  STRIP_ERROR_ID precedent). `aria-describedby` on the offending input points
 *  here, so the message is announced as that field's description. */
const AMOUNT_ERROR_ID = 'portal-amount-error';

type LoadState =
  | { kind: 'loading' }
  /**
   * ⚠️ T1746 M5 (B7) - THE HOME LIST NO LONGER RIDES ALONG, and this block used
   * to explain why it did. It said the operator picking a home from that list
   * WAS the account resolution, because `PORTAL_REQUEST_KEEP_KEYS` drops
   * `homeId` and nothing here could derive one. Both halves are now handled
   * SERVER-SIDE - the crossing derives the home from the request and the agreed
   * price is asked for by request id - so this screen reads no home list at
   * all, and `test/core/portal-home-read-census.spec.ts` is bidirectional: it
   * REDS if this file starts reading one again without a declared role.
   */
  /**
   * ⭐ T1746 M1 (B4) - `crossings` ARRIVES WITH THE REQUESTS, from the same
   * response, and is held in the same state object for the same reason the
   * homes are: a row that rendered "Make it a booking" while a crossing for it
   * was still in flight would offer a second press for a booking that already
   * exists.
   *
   * ⛔⛔ T1746 M3 (Captain's M2 gate item 2a) - `null` MEANS "NOT KNOWN", AND IT
   * IS A THIRD STATE, not an empty map. The kiosk answers it when its OWN
   * database could not be read. On it, this screen may claim nothing about any
   * row: not that it has crossed, and not that it has not. Every crossing
   * control and the landed-but-unbooked line are replaced by one neutral
   * sentence, because the alternative is a money screen asserting a fact it does
   * not have.
   */
  | {
      kind: 'ready';
      requests: PortalRequest[];
      crossings: Record<number, PortalCrossing> | null;
      /** ⭐ Board 2814 M4 (DD-11 row 14) - Helen's hand settlements, from the same
       *  read; `null` when the kiosk could not read them (the link-unknown case). */
      settlements: Record<number, PortalSettlement> | null;
      /** ⭐⭐ board 2637 (D4) - the nurse's note per request, from the same
       *  read. Rendered as a fact behind the tap and read by NOTHING else on
       *  this screen: not the "Note for the home" box, not any write. */
      nurseNotes: Record<number, string>;
    }
  | { kind: 'error'; message: string };

/**
 * Which actions a row offers, mirrored from the portal's LEGAL_TRANSITIONS as a
 * UI convenience only. The server is the authority: a stale screen just gets a
 * 409, which is handled.
 */
const ACTIONS: Record<string, Array<{ status: string; label: string }>> = {
  new: [
    { status: 'priced', label: 'Save price' },
    { status: 'declined', label: 'Decline' },
    { status: 'cancelled', label: 'Cancel' },
  ],
  priced: [
    { status: 'priced', label: 'Update price' },
    { status: 'confirmed', label: 'Confirm' },
    { status: 'declined', label: 'Decline' },
    { status: 'cancelled', label: 'Cancel' },
  ],
  confirmed: [{ status: 'cancelled', label: 'Cancel' }],
};

/** The two that end a request read as the quiet choice, so they are not
 *  competing with Save price at equal weight. */
const ENDING = new Set(['declined', 'cancelled']);

/** ⭐ board 2812 M3 fix round (Captain's layout item 2): the decline confirm names its
 *  verb and what the home sees (the nurse's word for `declined`, Words 3). */
const DECLINE_CONFIRM_LINE = 'Decline this request? The home sees Declined.';

/**
 * ⭐ board 2812 fix round (Captain's /check item 6) - THE CANCEL CONFIRM NAMES ITS VERB AND
 * WHAT THE HOME SEES, as the decline's does (the nurse's word for `cancelled` is
 * "Cancelled", `portal/src/client/copy.ts`). On a card where the home asked to CHANGE a
 * booked trip, Decline refuses the change and Cancel ends the trip, so there the question
 * says the whole trip goes. What happens to a booking is the scope line's (below).
 */
const CANCEL_CONFIRM_LINE = 'Cancel this request? The home sees Cancelled.';
const CANCEL_TRIP_CONFIRM_LINE = 'Cancel the whole trip, not only the change? The home sees Cancelled.';

/** ⭐ board 2812 fix round (item 6): accepting a home's CANCEL ask (or a repeating
 *  trip's STOP) closes its request, so the confirm says what the home sees then. */
const ASK_CANCEL_CONFIRM_LINE = 'Cancel the trip, as the home asked? The home sees Cancelled.';
const ASK_STOP_CONFIRM_LINE = 'Stop the repeating trip, as the home asked? The home sees Cancelled.';

/** ⭐ board 2812 fix round (item 6): the labels on a card with an open CHANGE ask, so the
 *  two red controls say which one refuses the change and which one ends the trip. */
const CHANGE_CARD_DECLINE = 'Decline the change';
const CHANGE_CARD_CANCEL = 'Cancel the trip';

/** ⭐ board 2812 fix round (item 6): a cancel whose booking links could not be read. The
 *  kiosk cancels a linked run with the request, so the screen cannot say what happens to
 *  a run it cannot see; it says how to find out instead. */
const CANCEL_LINKS_UNKNOWN_LINE = 'Whether this request has a booking could not be read. Check the booking after you cancel.';

/**
 * ⛔ T2088 M8 — THE SENTINEL `endingAct.status` FOR LEG A's CONFIRM, AND IT IS
 * DELIBERATELY NOT A REQUEST STATUS. `endingAct` exists so exactly one confirm is
 * armed at a time on a row; leg A cancels a BOOKING rather than transitioning the
 * request, so it needs a value that can never collide with an `ACTIONS` entry.
 * ⚠️ IT MUST STAY OUT OF `ENDING` and out of `ACTIONS`: `ENDING` keys the
 * destructive grammar of the REQUEST actions and `act()` would post this string
 * as a status if it ever reached one.
 */
const BOOKING_CANCEL_ACT = 'booking-cancel';

/**
 * ⭐ Board 2788 M6 - THE SENTINEL FOR ACCEPTING A CANCEL ASK, on `BOOKING_CANCEL_ACT`'s
 * reasoning: it reuses `endingAct` so exactly one confirm is up on a row, and it is
 * not a request status, so `act()` can never post it. ⛔ IT CONFIRMS IN PLACE: the
 * accept cancels the request AND ends the run, which is destructive (DESIGN.md §5.1),
 * so it is never a gold primary and never fires on the first tap.
 */
const ASK_CANCEL_ACT = 'ask-cancel-accept';

/**
 * ⭐ board 2812 M3 fix round 3 (F3-2, SC-6) - THE SENTINEL FOR DECLINING A HOME'S ASK, on
 * `ASK_CANCEL_ACT`'s reasoning. A decline is destructive, so once the reason is picked
 * the ask's Decline swaps the row for "Yes, decline it" / "Keep it" (the request's own
 * Decline confirm), and `Keep it` swaps it back. Not a request status: `act()` never
 * posts it.
 */
const ASK_DECLINE_ACT = 'ask-decline';

/**
 * ⭐⭐ T1749 M7 (R4) — THE ROW'S PENDING DECISION, WHICH IS THE ONE GOLD PRIMARY.
 *
 * ⛔ ONE ENTRY PER STATUS THAT HAS ONE, AND `confirmed` IS DELIBERATELY ABSENT.
 * A confirmed request is waiting on nothing: its only remaining action is to
 * cancel it, and a gold `Cancel` would be the system asking Helen to destroy
 * something. An open row whose status has no entry here renders no primary at
 * all, which DESIGN.md §5.2 permits ("at MOST one") and this screen needs.
 * ⛔ THE VALUES ARE `ACTIONS`' OWN STATUS KEYS, so a label rewording cannot
 * silently move the gold and a new action cannot inherit it by position.
 */
const PRIMARY_ACTION: Record<string, string> = {
  new: 'priced',
  priced: 'confirmed',
};

/**
 * ⭐⭐ T1749 M7 (C6) — THE CANCEL FACT, AS ONE LITERAL WITH TWO READERS.
 *
 * It was a standing amber paragraph at the top of this screen, said to every row
 * at once whether or not any of them had crossed; Alex: "we need to get rid of
 * this text, it gives this screen a cluttered view". The FACT is not dropped —
 * ending a request here writes `portal.requests.status` in the PORTAL's database
 * while billing reads `bookings.status` in the KIOSK's, so a crossed run is still
 * going ahead and still on the month's bill — it moves into the confirm limb of
 * the tap it is about, where the booking can be named.
 * ⛔ ONE LITERAL BECAUSE THE CONFIRM RENDERS IT TWO WAYS (with and without a
 * booking reference) and two copies would drift the moment one was reworded.
 * ⭐ board 2814 M6 (DD-8): it said "If the run is already in the calculator";
 * nursing runs are not keyed in the calculator any more, so it names any booking
 * for the run (the linked one, or one booked another way).
 */
const ENDING_SCOPE_LINE =
  'Ending it here only ends the request. Any booking for this run is still going ahead, and still on the month\'s bill.';

/**
 * ⭐⭐ T2088 M8 (R-4, step 3) — THE SENTENCE ABOVE IS NOW FALSE IN ONE CASE, AND
 * THIS IS THE FUNCTION THAT KNOWS WHICH.
 *
 * Leg B cancels the linked booking in the same handler as Helen's kiosk CANCEL,
 * so on a crossed row “the run is still going ahead, and still on the month's
 * bill” stopped being true the moment that landed. It is still true for every
 * other shape, and telling her the run is off when it is not is the sharpest
 * lie this screen could tell — so the fact is computed rather than chosen.
 *
 * ⛔ IT TAKES THE ACT AS WELL AS THE BOOKING, AND THAT IS NOT BELT-AND-BRACES.
 * This limb renders for BOTH ending acts, and leg B fires on `cancelled` ONLY:
 * a DECLINE on a crossed row leaves the run live exactly as before. Keyed on the
 * booking alone, the copy would promise a cancellation that a decline never
 * performs. (The plan's step 3 named only the linked/unlinked axis; the act is
 * the second one, and the plan errata record it.)
 *
 * ⛔ ONE FUNCTION, NOT TWO SCATTERED STRINGS — the property the constant above
 * was extracted for in the first place (T1749 M7 C6), kept rather than lost.
 * ⚠️ THE UNLINKED WORDS ARE UNCHANGED, byte for byte, because nothing about the
 * unlinked case moved.
 */
function endingScopeLine(
  actStatus: string,
  linked: { ref: string; live: boolean; travelled: boolean; keepsOpen: boolean } | null,
  linksUnknown: boolean,
): string | null {
  // ⭐ board 2812 fix round (Captain's /check item 6): with NO booking there is no run to
  // speak of, for a cancel as for a decline (F3-7), so nothing is said. With the links
  // unreadable a decline keeps the standing words (it never touches a booking), and a
  // cancel says how to find out, because the kiosk cancels a linked run with it.
  if (linked === null) {
    if (!linksUnknown) return null;
    return actStatus === 'cancelled' ? CANCEL_LINKS_UNKNOWN_LINE : ENDING_SCOPE_LINE;
  }
  // ⭐⭐ Board 2814 M8R1 (Captain's ruling on Q287; fix round 1, CR-2): a run that keeps
  // its request open (`travelledRunKeepsRequest`, the server's own predicate, so a
  // `completed` travelled run too) refuses both ending acts before any write, so the
  // limb says so and offers no "Yes" (`runTravelled` at the row). FIRST, ahead of the
  // liveness gate, because `completed` is not live there and is still held here.
  if (linked.keepsOpen) return TRAVELLED_REQUEST_COPY;
  // ⛔⛔ THE LIVENESS GATE, AND IT IS THE THIRD AXIS — ADDED AT THE M8 REVIEW
  // WAVE AFTER THREE REVIEWERS FOUND THE SAME LIE INDEPENDENTLY. Keyed on
  // "has it EVER crossed?" alone, this function promised "Ending it here cancels
  // that run too" about a booking that is already `cancelled` or `completed`.
  // ⛔ THE `completed` CASE IS THE SHARP ONE, and it is REACHABLE:
  // `LEGAL_TRANSITIONS.confirmed = ['cancelled']`, so a confirmed request whose
  // run has already TRAVELLED still renders this confirm. Helen was pre-told the
  // job "comes off the month's bill", then leg B's `cancelBooking` threw
  // `BookingNotCancellableError` and the banner told her the opposite — a promise
  // and its outcome in direct contradiction, on a money-bearing act, which is the
  // exact class this screen's own comments call the sharpest lie it can tell.
  // ⛔ THE PREDICATE IS THE SHARED ONE the control's own visibility uses 200 lines
  // below. Two rules that must agree, read from one expression.
  // ⭐ Board 2814 M4 (Captain ruling (c), 2026-10-06): a TRAVELLED run stays on the
  // month's bill from every Requests-screen control; since M8R1 its request stays open
  // too (the `keepsOpen` arm above). Booking detail's acknowledged cancel is the only
  // correction path.
  if (!linked.live) {
    return `This request is already booking ${linked.ref}, and that run is no longer live. Ending it here only ends the request.`;
  }
  if (actStatus !== 'cancelled') {
    // A decline: the booking is untouched, so the standing words still hold and
    // the row's own `End the run` control is the affordance for it.
    return `This request is already booking ${linked.ref}. ${ENDING_SCOPE_LINE}`;
  }
  return `This request is already booking ${linked.ref}. Ending it here cancels that run too, so it comes off the month’s bill.`;
}

/**
 * ⛔ THE ONE PLACE LEG B's OUTCOME BECOMES WORDS. Kept beside `endingScopeLine`
 * rather than inline in `act`, because the two are the same promise said at two
 * moments — before the tap and after it — and a reader checking that the screen
 * does not promise one thing and report another has to find both.
 * ⛔ THE SERVER'S SENTENCE IS NEVER RENDERED (SEC-5); `invoicedOn` is a CODE and
 * an invoice number we allocated. Every arm below names the booking and, where
 * there is one, the remedy.
 */
function bookingNotice(
  r: { homeName: string },
  status: string,
  outcome: PortalRequestBookingOutcome | null,
): { kind: 'ok' | 'error'; text: string } {
  // ⭐ Board 2814 M2: the arms live in `portalRunNotice` (lib/actions.ts) now, so a
  // cancel ask answered on booking detail words the run exactly as this row does.
  return portalRunNotice(`${r.homeName}: ${word(STATUS_WORDS, status)}.`, outcome);
}

/** Rows that can still be priced show the euro field. */
const PRICEABLE = new Set(['new', 'priced']);

/**
 * ⭐⭐ T1683 M1 — WHAT THE AGREED-PRICE LOOKUP HAS SAID ABOUT ONE ROW.
 *
 * ⛔ NINE STATES, NOT A NULLABLE NUMBER, and every one of them is a different
 * sentence on screen. ⚠️ T1746 M5 (B7): six of them are now the SERVER'S OWN
 * union, carried through verbatim from `agreedPriceFor` rather than re-derived
 * here - `priced` / `unpriced` / `missing` (D-RESOLVE's three, and the repo's
 * own header says why collapsing the last two is wrong), plus `unlinked`,
 * `no-destination` and `unknown-home`, which used to be states this screen
 * computed for itself out of the home pick it no longer has.
 *
 * ⛔ THE SCREEN ADDS ONLY `none`, `checking` AND `failed`, for the reason the
 * Quote screen already paid for: a hung lookup and a failed one both rendered as
 * "no price on file" there, so Helen hand-priced a run that HAS an agreed
 * figure. `failed` is ACTIONABLE and `missing` is not, so they must not look
 * alike, and neither may be invented server-side.
 *
 * ⛔ `oneWayCents` IS NAMED FOR WHAT IT IS AND `totalCents` IS THE SERVER'S
 * (S9). The captured figure is a ONE WAY price, nothing in `account_prices`
 * records a return, and the multiplier is sheet-scope - so the doubling happens
 * ONCE, in the handler, and this screen renders `totalCents` without ever
 * multiplying anything. A `× 2` written here is a second place for the rule to
 * be forgotten, on a figure that would undercharge a home by half.
 */
type PriceLookup =
  | { kind: 'none' }
  | { kind: 'checking' }
  | { kind: 'failed' }
  | AgreedPrice;

/**
 * ⚠️ T1746 M5 (B7) - THERE IS NO `RowPricing` OBJECT ANY MORE, AND THE ROW'S
 * WHOLE PRICING STATE IS THE ANSWER ABOVE.
 *
 * It used to hold four keys: a destination LIST, a "could not read the list"
 * flag, the destination the operator had chosen, and the lookup. All four
 * existed to support the destination picker, which existed to supply the key
 * D-RESOLVE needed; the offer route now derives that key from the request
 * itself, so there is one read, one answer and nothing to keep beside it.
 * ⛔ THE RULE THE DELETED `destinations` KEY CARRIED IS UNCHANGED and belongs
 * to whatever asks next: the picker list carried NO figure, because a list that
 * carried figures would be one account's price book in a browser, searchable
 * for a price without ever asking the resolver - the bypass
 * test/core/account-prices-resolve.spec.ts pins.
 *
 * ⛔⛔ AND THE PROHIBITION THE DELETED BLOCK EXISTED FOR, KEPT VERBATIM BECAUSE
 * NOTHING ABOUT IT CHANGED:
 *
 *   ⛔⛔ THERE IS NO `returnTrip` HERE ANY MORE, AND ITS ABSENCE IS THE POINT
   * (T1683 M7, A4). M1 kept an OPERATOR tick in this state: it doubled the
   * figure Helen was about to send and wrote nothing, so the booking's own
   * `return_trip` stayed false while the money said otherwise - one question
   * with two answers, and no row at all recording the tick.
   *
   * ⛔ THE ANSWER IS THE NURSE'S AND IT ARRIVES ON THE REQUEST
   * (`PortalRequest.returnTrip`, off the seam). It is therefore READ, never held
   * as working state: a control HERE would be a second answer again, and the
   * one an operator gives on THIS screen is the one that is guessing.
   *
   * ⛔⛔ THAT IS A CLAIM ABOUT **THIS SCREEN ONLY**, AND AN EARLIER REVISION OF
   * THIS BLOCK OVERSTATED IT. It said a kiosk override "would not reach the
   * booking any more than M1's did". IT DOES REACH IT. The kiosk QUOTE screen
   * still renders a live "Return trip" switch for a nursing job - REGISTRY-DRIVEN
   * through the generic `<Requirements>` (`shared/addons-schema.ts`,
   * `engineKey: 'returnTrip'`, `validFor: 'all'`), which is exactly why grepping
   * for M1's deleted `toggleReturn` finds nothing and reads as confirmation - and
   * it writes: `actions.ts` -> `persist-quote.ts` -> `bookings-repo.ts`
   * (`UPDATE bookings SET ... return_trip = $2`). Measured on real PostgreSQL
   * through the production functions at the M7 security review: an ordinary
   * quote save carrying `options.returnTrip:false` took a portal-set
   * `bookings.return_trip` from true to false. Authenticated-operator-only, so
   * it is a control question rather than an exposure - but it is NOT absent.
   *
   * ⭐ WHAT M7 CLOSED, AND WHAT IT LEFT OPEN, SEPARATED. Closed: the tick that
   * moved money without moving a row. What replaced it writes, and the nurse's
   * own answer survives it - `inquiries.return_trip` is written ONCE at the
   * crossing and never updated (⚠️ re-derived at T1746 M3: there are now THREE
   * `UPDATE inquiries` statements, status / customer_id+booking_id / ONE named
   * `extra` key (`agreedPriceCents`), and the census
   * `test/core/inquiries-return-trip-writer-census.spec.ts`
   * is what proves none of them writes the column), so
   * nurse-said vs operator-quoted is a two-row comparison, and `Quote.tsx`
   * badges the divergence "Edited" live against the immutable `customerFlags`
   * baseline. Open: an operator can still overrule the home, and doing so on a
   * one-way nursing job DOUBLES that home's invoice.
   *
   * ⚠️ DO NOT "RESTORE" A TICK HERE FOR THE CASE WHERE THE NURSE GOT IT WRONG.
   * Editing a request is a portal-side act, and the crossing re-reads the row
   * from the portal at write time, so a tick on THIS screen would still be
   * inert against the seam. LOCKING the `Quote.tsx` switch on a portal-sourced
   * booking is the live alternative and was deliberately NOT taken here: Helen
   * quotes every booking by phone, and a hard lock would leave her unable to
   * serve "actually, we need it to come back" at all. That is the owner's call.
   * Either way the nursing-home arms in
   * `test/client/manual-quote.spec.tsx` RED if that switch is locked or removed,
   * which is what sends the next reader back to this block.
 */

/**
 * The token an in-flight row read must STILL be holding when it answers.
 * Superseded by hand at each point the row's question changes - see
 * `supersede` and the block above it.
 */
type LiveToken = { live: boolean };

const EMPTY_LOOKUP: PriceLookup = { kind: 'none' };

/* ⚠️ Board 2814 M2 - `CROSSABLE` (priced and confirmed: the rows that may become a
   booking, never `new`, whose price is missing and whose portal transition to
   confirmed is illegal) is RETIRED: `needsHelenNow` offers Confirm on exactly those
   statuses (DD-11 rows 3, 5, 7, 8), and the server still refuses anything else
   with `crossing_not_priced`. */

/* ⭐⭐ T1746 M3 (Captain's M2 gate item 2a) - `CROSSINGS_UNKNOWN_LINE`, THE ONE
   SENTENCE A ROW SAYS WHEN THE KIOSK COULD NOT READ ITS OWN CROSSINGS, and board
   2814 M1's `PAST_LINE` moved to `lib/actions.ts` at board 2814 M2, beside
   `needsHelenNow`, which words those rows now. It claims nothing about the
   request; ONE literal, read by the row (through `needsHelenNow`) and by the
   `cross` handler's refusal, so the two cannot drift. */

/* ⚠️ Board 2814 M4 - `CASH_INTERIM_LINE` (M1's cash interim sentence) STOOD HERE
   AND IS RETIRED with the interim: cash crosses through the confirm step now. */

/**
 * ⭐⭐ Board 2814 M2 (plan DD-11) - WHERE THIS ROW LIVES AND WHAT IT RENDERS, read
 * from the one table (`needsHelenNow`) with this screen's crossings and clock. The
 * list split, the order, the default-open row, the gold and `cross`'s own guard
 * all read it, so none of them can disagree about a row.
 */
/** ⭐ Board 2814 M4 - the two kiosk-side reads a row's place depends on: its
 *  crossing and its hand settlement. The ready state carries both. */
type Links = {
  crossings: Record<number, PortalCrossing> | null;
  settlements: Record<number, PortalSettlement> | null;
};

function placeOf(
  r: PortalRequest,
  links: Links,
  now: Date,
): HelenPlace {
  const { crossings, settlements } = links;
  return needsHelenNow(
    r,
    crossings === null ? undefined : crossings[r.requestId],
    settlements === null ? null : settlements[r.requestId] ?? null,
    // ⭐ Board 2814 M4: an unreadable settlements map is the link-unknown case too.
    crossings !== null && settlements !== null,
    now,
  );
}

/**
 * ⭐⭐ Board 2814 M1 (plan DD-2, DD-3, DD-4; D1, D2, D10, D11) - WHAT THE ROW'S
 * CONFIRM DOES, AND WHETHER THERE IS ONE AT ALL. Read by the render AND by
 * `cross`, so the control and the handler's refusal cannot disagree.
 *   · `step` - the press books it AND confirms it: Confirm swaps in place to the
 *     confirm step and posts to the crossing route (DD-11 rows 3, 5, 7 and 8),
 *     for every payer since board 2814 M4 retired the cash interim.
 *   · `none` - every other row: no Confirm.
 * ⭐ Board 2814 M2: it is a reading of `needsHelenNow`'s primary now, no longer a
 * rule of its own, so the table and the control are one fact.
 * ⚠️ A PAYER THAT IS NEITHER CASH NOR A HOME'S ACCOUNT takes the step too: the
 * crossing refuses it in words (`crossing_payer_unknown`), which is better than a
 * row with no Confirm and nothing said.
 */
function confirmKindOf(
  r: PortalRequest,
  links: Links,
  now: Date,
): 'step' | 'none' {
  return placeOf(r, links, now).primary === 'confirm' ? 'step' : 'none';
}

/* ⚠️ Board 2814 M2 - `rendersGoldPrimary` and `saysPast` (board 2814 M1) STOOD HERE
   AND ARE RETIRED: the gold is `needsHelenNow`'s `gold`, and the past lines are its
   `line` (rows 2 and 4), so neither is a second rule beside the table. */

/**
 * ⭐ Board 2814 M1 (DD-2) - THE BOOKING'S DAY, IN THE SCREEN'S DUBLIN WORDS, or
 * null. The crossing's own day when the row already holds a booking; otherwise
 * the day the crossing books (the lead rule: the pickup's Dublin day, else the
 * request's), which is `crossingJourneyDate` on the server through the one
 * shared lead rule.
 */
function bookingDayWords(r: PortalRequest, crossing: PortalCrossing | undefined, bookingId: number): string | null {
  // ⭐ Board 2814 M2: the request's day is `portalRequestDay`, the crossing's own.
  const day = crossing !== undefined && crossing.bookingId === bookingId && crossing.bookingJourneyDate !== null
    ? crossing.bookingJourneyDate
    : portalRequestDay(r);
  if (day === null || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  return new Date(`${day}T12:00:00.000Z`).toLocaleDateString('en-IE', {
    ...DUBLIN,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * ⭐⭐ Board 2814 M1 (DD-1, DD-2, D7) - WHAT ONE CONFIRM PRESS DID, IN WORDS.
 * ⛔ EVERY FAILURE AFTER THE BOOKING SAYS THE BOOKING EXISTS, that the home's
 * request is not confirmed yet, and what to press: never a quiet success, and the
 * booking is never described as undone (nothing undoes it).
 * The booking is named by the reference it carries: the crossing's when the row
 * already held this booking, else the request's own reference (the crossing
 * carries it, never re-mints it), else the booking id.
 */
function crossingOutcomeNotice(
  r: PortalRequest,
  crossing: PortalCrossing | undefined,
  out: PortalCrossingOutcome,
): Notice {
  const ref = (crossing !== undefined && crossing.bookingId === out.bookingId ? crossing.jobRef : null)
    ?? (r.jobRef ?? null)
    ?? `#${String(out.bookingId)}`;
  switch (out.requestMarked) {
    case 'confirmed':
    case 'already': {
      const day = bookingDayWords(r, crossing, out.bookingId);
      return {
        kind: 'ok',
        text: `Confirmed. It is now booking ${ref}${day === null ? '' : ` for ${day}`}. The home sees Confirmed.`,
        openBookingId: out.bookingId,
      };
    }
    case 'home_cancelled':
      return {
        kind: 'error',
        text: `The home cancelled this while you were booking it. Booking ${ref} was made.`,
        openBookingId: out.bookingId,
      };
    case 'request_changed':
      // ⭐ Board 2788 M4 (DD-1)'s words, kept, with the booking named.
      return {
        kind: 'error',
        text: `Booking ${ref} was made. The home changed the request after the booking was made, so it was not confirmed. Check the booking\u2019s time, then press again.`,
        openBookingId: out.bookingId,
      };
    case 'unchanged':
      return {
        kind: 'error',
        text: `Booking ${ref} was made, but the home\u2019s request is not confirmed yet: the portal did not take it. Press Confirm again.`,
        openBookingId: out.bookingId,
      };
    default:
      return {
        kind: 'error',
        text: `Booking ${ref} was made, but the home\u2019s request is not confirmed yet: the portal did not answer. Press Confirm again.`,
        openBookingId: out.bookingId,
      };
  }
}

/** The screen's one announcement. `openBookingId` adds "Open the booking". */
type Notice = { kind: 'ok' | 'error'; text: string; openBookingId?: number };

/* ⚠️ board 2812 M3 fix round - `toneOf` (T1749 M7, R1/R4: the pill's tone, read off the
   shared `REQUEST_TONES` with the OPERATOR reading) STOOD HERE AND IS RETIRED: no chip on
   this screen is toned now. A Need you now row wears its ACTION and a Done row its status
   word, both in the neutral box (Captain's layout items 1 and 3). If a toned chip comes
   back, it takes `toneFor('request', s, 'operator')` (T2187), never the nurse map. */

const STATUS_WORDS: Record<string, string> = {
  new: 'New',
  priced: 'Priced',
  confirmed: 'Confirmed',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

/* ⭐ `PAYER_WORDS` (who pays, in Helen's words) LIVES IN `lib/payer-words.ts`
   since board 2764 M7, shared with the Invoices tab; its history and guards are
   recorded there. */

/**
 * Look a value up in one of the word tables above WITHOUT walking the prototype
 * chain.
 *
 * ⛔ WHY THIS EXISTS, measured: a row whose `status` was the string
 * `'constructor'` made `ACTIONS[r.status]` resolve to `Object.constructor` — a
 * Function, not an array — so `.map` was undefined, the render threw, and the
 * WHOLE screen went blank. Not the bad row: every row, plus the empty-state
 * copy and the error banner, so Helen would see a heading over nothing with no
 * way to tell why. That directly breaks the promise the projection module makes
 * one layer down ("one malformed field must not blank Helen's whole inbox").
 *
 * `status`, `payerKind` and `timeMeaning` are PG enums upstream so they cannot
 * carry this today — but `frequency` lives in UNCONSTRAINED JSONB, which is
 * exactly the reachable case.
 */
function word(table: Record<string, string>, key: string): string {
  return Object.hasOwn(table, key) ? table[key]! : key;
}

/**
 * Euro text -> whole cents, or null if it is not a FIGURE at all. Mirrors the
 * server's integer-strict rule rather than rounding a typo into a price.
 *
 * ⛔⛔ IT PARSES; IT DOES NOT SET THE FLOOR. It used to fold `cents > 0` in
 * here, which made a well-formed zero indistinguishable from "about a hundred"
 * — so a prefilled `0.00` was refused with "enter a whole amount above zero",
 * a sentence that tells Helen to INVENT a number for a run somebody agreed was
 * free. That is the exact harm `account-prices-repo.ts` warns about. The floor
 * still exists and is still enforced, in `act` below, where it can say WHY.
 *
 * ⚠️ SO A ZERO COMES BACK AS `0`, NOT `null`. `act` is the only caller
 * (grep-checked); any new one must decide what a zero means to it rather than
 * assuming this function already refused it.
 */
function parseEuro(text: string): number | null {
  const trimmed = text.trim().replace(/^€/, '').trim();
  if (trimmed === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
}

function formatEuro(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

/**
 * ⭐⭐ T1746 M5 (step 9; Captain's M3 gate item 6) - WHAT THE ROW'S PRICE COLUMN
 * SAYS, AND THE ONE THING A READER MUST NOT UNDO: ONCE A REQUEST HAS CROSSED,
 * THIS CELL FOLLOWS THE BOOKING.
 *
 * ⛔ THE DEFECT IT CLOSES, MEASURED 2026-09-05. After `Re-open in calculator`
 * moved booking 20 to 45.00, this screen still read 30.00 for the same job - the
 * request's FROZEN figure - while BookingDetail and the Bookings list both read
 * 45.00. B3 says one live figure per job, and Helen quotes off whichever screen
 * is in front of her.
 *
 * ⛔ THE REQUEST'S FIGURE IS NOT WRONG, IT IS OLD, and that is why it still
 * renders on an UNCROSSED row. `pricedAmountCents` is what the portal holds and
 * what the home was told; once a booking exists, the money fact is the QUOTE and
 * the portal's number is history. Rendering the request's figure on a crossed
 * row is what the mutation for this step does, and the differing-figures arm in
 * `portal-requests.spec.tsx` is what reds.
 *
 * ⛔ A CROSSED ROW WITH NO FIGURE RENDERS NOTHING, NOT THE REQUEST'S. `null`
 * means the booking has no quote yet - a real state, since the crossing creates
 * the booking before anyone prices it - and falling back to the request's figure
 * there would print a number that belongs to neither object. The cell defers to
 * the `Open booking` control already on the row, which is where the live answer
 * is.
 *
 * ⚠️ `crossing` IS `undefined` BOTH WHEN NOTHING CROSSED AND WHEN THE CROSSINGS
 * READ FAILED (`crossings === null`). Both fall back to the request's own
 * figure, which is correct: it is a fact about the REQUEST and claims nothing
 * about a booking this screen cannot see.
 */
function rowPriceText(r: PortalRequest, crossing: PortalCrossing | undefined): string {
  if (crossing !== undefined) {
    return crossing.latestQuoteCents === null ? '' : formatEuro(crossing.latestQuoteCents);
  }
  return r.pricedAmountCents === null ? '' : formatEuro(r.pricedAmountCents);
}

/**
 * ⚠️ T1746 M5 (B7) - `kioskDestinationKey` LIVED HERE AND HAS MOVED SERVER-SIDE,
 * not been dropped. It read `r.kioskDestinationId` and refused anything that was
 * not a positive safe integer, because `account_prices` is keyed on the KIOSK's
 * `destinations` and both id spaces are small overlapping BIGINTs - so a wrong
 * key does not miss, it resolves a real but WRONG destination and shows Helen
 * another place's agreed price. That guard is now the offer handler's
 * (`handlePortalRequestAgreedPrice`, which answers `no-destination` when the key
 * is absent or unusable), and this screen never touches the key at all.
 * ⛔ DO NOT REINSTATE A CLIENT-SIDE COPY: two positivity guards on one money key
 * is two places for one of them to drift.
 */

const FREQUENCY_WORDS: Record<string, string> = {
  weekly: 'Every week',
  fortnightly: 'Every two weeks',
  every_n_weeks: 'Every few weeks',
  monthly_date: 'Monthly, on a date',
};

const WEEKDAY_WORDS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MEANING_WORDS: Record<string, string> = {
  appointment: 'appointment time',
  collection: 'collection time',
};

/**
 * When the journey is.
 *
 * ⛔ THE TIME OF DAY IS NOT OPTIONAL, and an earlier version of this function
 * dropped it in BOTH shapes. `journey_date` is TIMESTAMPTZ, so a
 * `toLocaleDateString` render showed the day and silently discarded whether the
 * bus is wanted at 07:00 or 18:00 - and Helen prices on exactly that. For a
 * recurring request the time lives only inside `schedulePattern.time`, because
 * the table's `one_off_xor_pattern` CHECK means a recurring row has NO
 * `journey_date` at all.
 *
 * ⚠️ PINNED TO Europe/Dublin, not the host locale. This is an Irish operator
 * pricing Irish journeys; a kiosk, a container or a reviewer's laptop on
 * another timezone would otherwise render a different hour for the same booking
 * and nothing on screen would say so. `en-IE` fixes the day/month order for the
 * same reason.
 */
const DUBLIN = { timeZone: 'Europe/Dublin' } as const;

/**
 * ⭐⭐ board 2637 (plan D1; Alex's ruling 1, 2026-09-30) - THE ROW LEADS WITH THE
 * COLLECTION. Helen after LCC's first request: "we need to see the collection
 * time, not the appointment time". So an `appointment` row that carries a
 * pickup leads with the PICKUP, labelled "(collection time)", and the
 * appointment drops to the small line under it (`appointmentText`). This is the
 * SAME lead rule the crossing writes into the booking (`crossingLead` in
 * server/app.ts), so the row and the booking it becomes say the same time.
 * ⛔ An appointment row with NO pickup keeps "(appointment time)" and invents no
 * collection; a `collection` row already leads with its own time.
 * ⭐ Board 2647 (K2): the predicate is `shared/portal-lead.ts`, the one copy the
 * crossing calls too; this screen keeps no validity check of its own.
 */
function leadsWithPickup(r: PortalRequest): boolean {
  return portalRequestLead(r).kind === 'pickup';
}

function whenText(r: PortalRequest, withMeaning = true): string {
  if (leadsWithPickup(r)) {
    return withMeaning
      ? `${formatDublinInstant(r.pickupAt) as string} (${word(MEANING_WORDS, 'collection')})`
      : formatDublinInstant(r.pickupAt) as string;
  }
  // ⭐ board 2812 M3 fix round 2 (UX-8): a Done row's head reads the day and time alone.
  const meaning = withMeaning && r.timeMeaning ? ` (${word(MEANING_WORDS, r.timeMeaning)})` : '';

  const p = r.schedulePattern;
  if (p) {
    const day = p.weekday !== null ? (WEEKDAY_WORDS[p.weekday] ?? '') : '';
    const freq = word(FREQUENCY_WORDS, p.frequency);
    let text: string;
    if (p.frequency === 'monthly_date') {
      text = `Monthly on day ${String(p.dayOfMonth ?? '')}, ${p.time}`;
    } else if (p.frequency === 'every_n_weeks') {
      text = `Every ${String(p.intervalWeeks ?? '')} weeks, ${day} ${p.time}`.replace('  ', ' ');
    } else {
      text = `${freq}, ${day} ${p.time}`.replace('  ', ' ');
    }
    return `${text.trim()}${meaning}`;
  }

  if (!r.journeyDate) return 'Not given';
  const d = new Date(r.journeyDate);
  if (Number.isNaN(d.getTime())) return 'Not given';
  const when = d.toLocaleString('en-IE', {
    ...DUBLIN,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${when}${meaning}`;
}

/**
 * ⭐⭐ T1717 M6 (DD5, P0-7, surface 1 of the 19) - **WHEN** THE BUS IS WANTED
 * BACK, on the row Helen prices from.
 *
 * ⛔ BOTH CONDITIONS, AND `returnTrip` IS THE OUTER ONE. The flag is what the
 * destination cell's `(and back)` already renders and what doubles the figure;
 * this is the second half of the same fact. A row that printed a time without
 * the flag would say the job comes back in a place the operator is not looking,
 * while the cell she IS looking at says one way.
 *
 * ⛔ NULL FOR A RECURRING REQUEST, BY CONSTRUCTION AND NOT BY OVERSIGHT. DD4
 * leaves the recurring return time a named residual: the pattern carries no
 * return key, so a standing weekly run tells Helen THAT it comes back and not
 * when. That is the honest rendering of what the nurse was asked.
 *
 * ⭐⭐ T2088 M7 (plan D5) ADDS THE THIRD ARM, AND IT IS THE ONE THIS ROW WAS
 * MISSING. D5 ruled that a return whose time is not yet known is priced as a
 * FULL return - a return is a return - so the figure beside this line is already
 * doubled. Until now the row said `(and back)` and NOTHING about the time, which
 * is the same thing it says for the recurring residual: Helen could not tell a
 * time somebody promised to ring back with from a time nobody ever recorded.
 * `returnAtState === 'to-confirm'` is the only thing on this side that tells
 * those apart.
 * ⛔ AND THE `'unset'` ARM IS UNCHANGED, DELIBERATELY. A timeless return whose
 * marker is `'unset'` keeps today's silence, for the reason M4 Step 5 gives on
 * the nurse's own list: it is a pre-v14 row or a standing run whose pattern
 * captures no instant, and neither was ever promised a call. Do not "improve" it
 * into the new phrase - that would put a promise on Helen's screen that nobody
 * made to the home.
 * ⛔ THE WORDS COME FROM `shared/dublin-date.ts`, which carries the ruling that
 * they are the PORTAL's words verbatim.
 */
function backText(r: PortalRequest): string | null {
  if (!r.returnTrip) return null;
  return formatReturnWhen(r.returnAt, r.returnAtState);
}

/**
 * ⭐⭐ board 2637 (plan D1) - THE APPOINTMENT, SMALL, UNDER THE COLLECTION.
 *
 * ⛔ ONLY WHEN THE ROW LEADS WITH THE PICKUP (`timeMeaning === 'appointment'`
 * AND a pickup), so a stray `pickupAt` on any other row can never label the
 * collection as the appointment. Null otherwise, and the line is omitted.
 *
 * ⛔ THE DAY RIDES WITH IT ONLY WHEN IT DIFFERS from the collection's, the rule
 * the portal's own operator table follows (`portal/src/client/screens/Ops.tsx`
 * `whenCell`): offsets reach 480 minutes, so an early appointment's pickup can
 * fall on the previous day, and a bare clock would borrow the wrong one.
 *
 * ⛔ IT REPLACES THE `Pickup` FACT THAT SAT BEHIND THE TAP (T1868 M5 to board
 * 2637). The lead line now says the pickup, and Alex's rider is "no fact on one
 * card twice". What is rendered is still what the NURSE left in the box; the
 * offset is not on this seam, so nothing here derives it.
 */
function appointmentText(r: PortalRequest): string | null {
  if (!leadsWithPickup(r) || r.journeyDate === null) return null;
  const appointment = new Date(r.journeyDate);
  if (Number.isNaN(appointment.getTime())) return null;
  const sameDay = dublinCivilDate(appointment) === dublinCivilDate(new Date(r.pickupAt as string));
  const text = sameDay
    ? appointment.toLocaleString('en-IE', { ...DUBLIN, hour: '2-digit', minute: '2-digit' })
    : formatDublinInstant(r.journeyDate);
  return text === null ? null : `Appointment ${text}`;
}

/**
 * ⭐⭐ board 2637 (plan D2) - WILL THE BOOKING CROSS WITH NO TIME? The crossing's
 * lead rule answers the time from the pickup (an appointment row) or from the
 * request's own time (a collection row), and from NOTHING otherwise, so the
 * confirm step says so before she presses, as it already does for a missing date.
 */
function crossesWithoutTime(r: PortalRequest): boolean {
  return portalRequestLead(r).kind === 'none';
}

/**
 * ⭐ T1746 M1 (B4) - HOW A CROSSED BOOKING IS NAMED ON THIS SCREEN.
 *
 * ⛔ THE JOB REFERENCE FIRST, because that is the token both sides of a phone
 * call can read (Q109); the booking id is the fallback for a booking made
 * before references existed. `#` marks it as an internal number rather than
 * letting a bare integer read like a reference that failed to load.
 *
 * ⚠️ CALLED ONLY WITH `bookingId` NON-NULL. The landed-but-unbooked row has no
 * booking to name at all and renders its own sentence instead.
 */
/**
 * ⭐⭐ Board 2814 M2 (DD-11) - IS THIS ROW IN NEEDS YOU NOW? It replaced 2788 M6's
 * retired `waitsOnHelen`: the table is one fact for the badge, the order and the
 * default-open row. ⛔ IT DOES NOT RANK BY GOLD: inside each list the order is by
 * time (DD-11 item 2), so a cancel ask for tomorrow sits above a price for next
 * month. The default-open row prefers a gold row on its own (`defaultOpenOf`).
 */
function needsHelen(
  r: PortalRequest,
  links: Links,
  now: Date,
): boolean {
  return placeOf(r, links, now).place === 'needs';
}

/**
 * ⭐⭐ T1749 M7 (R4) — WAITING-ON-YOU FIRST, THEN BY TIME, and it is done HERE
 * rather than on the server.
 *
 * ⛔ THE PORTAL'S ORDER IS UNTOUCHED. `listPortalRequests` returns the portal's
 * own newest-first list and other readers depend on it; R4 is a statement about
 * what HELEN sees on this screen, so the sort is client-side and local. A server
 * sort would re-order a seam three other consumers read.
 *
 * ⛔ "WAITING ON YOU" IS NEEDS YOU NOW (`needsHelen`, board 2814 M2, DD-11), not
 * a hand-written status set: the same table the count reads, so the order and
 * the count can never disagree. The screen renders the two lists apart, each in
 * this order, which inside a list is by time.
 *
 * ⛔ AND THE TIE-BREAK IS THE REQUEST ID, which is the only total order this
 * screen holds. `journeyDate` is nullable (a repeating request has none), so two
 * undated rows would otherwise compare equal and `sort` is not required to be
 * stable for equal keys. Undated rows sort AFTER dated ones: they have no day to
 * be early or late for.
 */
function orderForHelen(
  requests: readonly PortalRequest[],
  links: Links,
  now: Date,
): PortalRequest[] {
  // ⭐⭐ T1749 M7 wave-3 (ux BLOCK 2, impeccable P1-4) — "WAITING ON YOU" IS THE
  // PENDING DECISION, NOT "HAS ANY ACTION AT ALL". The first cut keyed on
  // `ACTIONS`-has-an-entry, which is TRUE of `confirmed` too — its one action is
  // Cancel — so with 25 new and 24 confirmed rows the list interleaved them by
  // date, the first three rows were settled ones and the screen opened on a row
  // with no gold primary at all. The order, the default-open row and the gold
  // button are one fact rather than three that can disagree.
  // ⭐ Board 2788 M6, board 2814 M1, then M2: that fact is Needs you now, the
  // DD-11 table, never `PRIMARY_ACTION`-has-the-status.
  // ⭐ Board 2814 M2: Needs you now first (`needsHelen`), then the rest; by time inside.
  const waiting = (r: PortalRequest): number => (needsHelen(r, links, now) ? 0 : 1);
  // ⭐⭐ board 2637 (plan D1) - BY THE LEAD INSTANT, the one the row's big line
  // shows: the pickup on an appointment row that has one, else the request's own
  // instant. ⛔ Compared as INSTANTS, not strings: offsets differ per home, so
  // an earlier appointment can have the later collection, and the pickup and
  // the appointment need not share a serialisation.
  const lead = (r: PortalRequest): number | null => {
    const iso = leadsWithPickup(r) ? r.pickupAt : r.journeyDate;
    if (iso == null) return null;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : t;
  };
  return [...requests].sort((a, b) => {
    const byWaiting = waiting(a) - waiting(b);
    if (byWaiting !== 0) return byWaiting;
    const da = lead(a);
    const db = lead(b);
    if (da !== db) {
      if (da === null) return 1;
      if (db === null) return -1;
      return da - db;
    }
    return a.requestId - b.requestId;
  });
}

/**
 * ⭐⭐ Board 2814 M2 (DD-11) - THE TWO LISTS: Needs you now, and the quiet Done list
 * below it, each in `orderForHelen`'s order. A request that is a booking now (row
 * 11) is in neither: it is not listed.
 */
function splitForHelen(
  requests: readonly PortalRequest[],
  links: Links,
  now: Date,
): { needs: PortalRequest[]; done: PortalRequest[] } {
  const needs: PortalRequest[] = [];
  const done: PortalRequest[] = [];
  for (const r of orderForHelen(requests, links, now)) {
    const where = placeOf(r, links, now).place;
    if (where === 'needs') needs.push(r);
    else if (where === 'done') done.push(r);
  }
  return { needs, done };
}

/**
 * ⭐⭐ Board 2814 M2 (DD-11) - THE ROW THE SCREEN OPENS ON WHEN NOBODY HAS CHOSEN:
 * the first Needs you now row, in its time order, that carries a gold primary;
 * else the first Needs you now row; else none. ⛔ THE GOLD PREFERENCE IS HERE AND
 * NOT IN THE ORDER: board 2814 M1's measured lesson (the screen opened on a row
 * with no gold, the seat's SC-10c red) is kept without re-ordering the list.
 */
function defaultOpenOf(
  needs: readonly PortalRequest[],
  links: Links,
  now: Date,
): number | null {
  const gold = needs.find((r) => placeOf(r, links, now).gold);
  return (gold ?? needs[0])?.requestId ?? null;
}

function bookingRef(c: PortalCrossing): string {
  return c.jobRef ?? `#${String(c.bookingId)}`;
}

// ---------------------------------------------------------------------------
// ⭐⭐ Board 2788 M6 (steps 2 and 4) - THE HOME'S CHANGE AND ITS ASKS, ON HELEN'S ROW
// ---------------------------------------------------------------------------

/**
 * The mark beside the status pill, so a row waiting on her answer cannot be
 * missed on a closed list: "Change asked", "Cancel asked" or "Changed" (DD-14:
 * only while New or Priced). ⛔ A NEUTRAL chip, never a toned one: these are not
 * statuses, and a toned chip must carry a word from the status-word registry
 * (`conformance.spec.tsx`'s chip arm).
 */
function changeMarkOf(r: PortalRequest): string | null {
  if (r.askState === 'open' && r.askKind === 'change') return 'Change asked';
  if (r.askState === 'open' && r.askKind === 'cancel') return 'Cancel asked';
  if (r.homeChanged === true && (r.status === 'new' || r.status === 'priced')) return 'Changed';
  return null;
}

/* ⚠️ Board 2814 M2 - `askLineOf`, `historyLineOf`, `DECLINE_REASON_WORDS` and
   `askNotice` (board 2788 M6), with their clock and span helpers, MOVED to
   `lib/actions.ts` unchanged: booking detail answers the same ask and shows the
   same history (DD-11 item 4), and a second copy of each sentence would drift. */

/**
 * The confirm-in-place limb, WITH THE KEYBOARD FOLLOWING IT.
 *
 * ⛔ WHY IT IS A COMPONENT AT ALL, and it is the only reason: the limb is
 * rendered inside a `.map()` over a row's actions, and a hook cannot be called
 * there. Mounting it as its own component is what buys the ONE `useEffect` that
 * moves focus; nothing else about the markup changed.
 *
 * ⛔⛔ THE FOCUS MOVE IS THE FIX (T1749 M7 wave-4, ux WARN 2). The limb REPLACES
 * the button that armed it, so the element the keyboard was on is removed from
 * the DOM — and a removed `activeElement` falls back to `<body>`. Measured on
 * the live kiosk: Tab to `Decline`, Enter, and `document.activeElement` was
 * BODY, so the next Tab restarted at the top of the document and the question
 * just asked was announced to nobody. WCAG 2.4.3-adjacent.
 * ⚠️ THE FIRST CONTROL BY DOM ORDER, not a named one, and reading the position
 * rather than the label keeps this true if the copy is re-ruled.
 * ⭐⭐ board 2812 fix round (Captain's /check item 9): EVERY ENDING LIMB PUTS ITS KEEP
 * FIRST ("Keep it", "Keep the run"), in an `ActionRow` with the red "Yes ..." in the
 * end slot at the far end, so the first control, and the focus, is the safe answer: a
 * double Enter keeps the request. Before, the request's Decline and Cancel and the
 * ask's Decline put "Yes" first and the focus landed on it.
 * ⭐ Board 2814 M1: a `select` counts as a control too, so the confirm step
 * lands on "How many are travelling?", its first question, rather than on the
 * button that cannot succeed until it is answered. The two ending limbs carry no
 * `select`, so their first control is unchanged.
 * ⚠️ ON MOUNT ONLY (`[]`). A ref callback would re-fire on every render of the
 * open row and yank focus back off `Keep it` while the operator was reading it.
 */
function ConfirmInPlace({ children, endSlotAt }: {
  children: ComponentChildren;
  /** ⭐ board 2812 M3 fix round: the limb replaces the card's whole ActionRow now, so
   *  `Keep it` restores focus to the control that armed it BY ITS POSITION in that
   *  row's end slot (0 = the first ending act), never by its words. */
  endSlotAt?: number;
}) {
  const limb = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = limb.current;
    if (node === null) return undefined;
    const row = node.closest('li');
    const host = node.parentElement;
    const at = host === null ? -1 : [...host.children].indexOf(node);
    node.querySelector<HTMLElement>('select, button')?.focus();
    // ⛔⛔ AND THE MIRROR: `Keep it` UNMOUNTS this limb, which takes the focused
    // element with it, so without this the keyboard fell back to `<body>` a
    // second time and the next Tab restarted at the top of the document. The
    // arming half was fixed at wave 4 and the dismissing half was not (ux round
    // 3): one transition, two directions, and only one of them was answered.
    return () => {
      // ⛔ A MICROTASK, NOT A DIRECT CALL. This cleanup runs DURING the diff that
      // removes the limb; the control that replaces it may not be in the document
      // yet, and focusing nothing is what "activeElement is body" already means.
      queueMicrotask(() => {
        const back = (endSlotAt === undefined ? null : restoredEndControl(host, at, endSlotAt))
          ?? restoredControl(host, at) ?? row?.querySelector('button[aria-expanded]') ?? null;
        if (back instanceof HTMLElement) back.focus();
      });
    };
  }, []);
  return (
    <div class="portal-request-confirm" ref={limb}>
      {children}
    </div>
  );
}

/**
 * The control that took the limb's place, BY DOM POSITION rather than by label.
 *
 * ⛔ POSITION, NOT `Decline` / `Cancel`: the two words are net-locked names and a
 * focus restore keyed on copy breaks the day the copy is re-ruled. It is also
 * what the arming half does (the limb's FIRST button, not a named one), so the
 * two directions read the same.
 * ⚠️ A WALK FORWARD, NOT `children[at]`, and the reason is the separator. The
 * armed limb replaces a FRAGMENT, and for `Decline` that fragment is two nodes —
 * `<hr class="section-rule">` then the button — so the index the limb occupied
 * belongs to the rule once the fragment is back. The first element at or after
 * that index which IS or CONTAINS a button is the control that armed it.
 */
/** The `n`th control in the end slot of the action row that took the limb's place. */
function restoredEndControl(host: Element | null, at: number, n: number): Element | null {
  if (host === null || at < 0 || !host.isConnected) return null;
  for (const child of [...host.children].slice(at)) {
    const slot = child.querySelector('[data-slot="end"]');
    if (slot !== null) return slot.querySelectorAll('button')[n] ?? null;
  }
  return null;
}

function restoredControl(host: Element | null, at: number): Element | null {
  if (host === null || at < 0 || !host.isConnected) return null;
  for (const child of [...host.children].slice(at)) {
    if (child instanceof HTMLButtonElement) return child;
    const inner = child.querySelector('button');
    if (inner !== null) return inner;
  }
  return null;
}

export function PortalRequests({
  onChanged,
  pollTick,
  onOpenBooking,
  registry,
  homes,
  suppressOpen,
  onOpenRow,
  openRequestId,
  inTodo,
  arrange,
  homeKey,
  onStale,
  beside,
}: PortalRequestsProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [notice, setNotice] = useState<Notice | null>(null);
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  // ⚠️ ONE error at a time, tagged with the ROW it belongs to - not a
  // per-row map rendered beside each input. `.field-error` is a PORTAL class
  // and does not exist in this stylesheet at all, so porting that shape would
  // have rendered unstyled text. The kiosk idiom is a single `<Banner>` with an
  // id and `aria-describedby` pointing at it from the offending control
  // (RateList.tsx's STRIP_ERROR_ID), which also matches the fact that only one
  // action is ever in flight here.
  const [amountError, setAmountError] = useState<{ requestId: number; text: string } | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  /**
   * Which row has its confirm step open, by request id (board 2814 M1: the
   * step Confirm swaps to, which books the request and confirms it).
   *
   * ⛔ ONE AT A TIME, and it is the same shape `AccountInvoices` uses for its
   * withdrawal confirm: two open crossings would let Helen read one row's
   * evidence sentence while pressing another row's button.
   */
  const [crossingId, setCrossingId] = useState<number | null>(null);
  /**
   * ⭐ Board 2814 M1 (DD-4) - THE SAME-DAY BOOKINGS THE DUPLICATE GUARD FOUND,
   * for the one row whose step is answering them. Rendered INSIDE that step with
   * "Book it anyway"; cleared when the step closes or is opened afresh.
   */
  const [duplicate, setDuplicate] = useState<{ requestId: number; matches: CrossingDuplicate[] } | null>(null);
  /** The band per row. The nurse's form has no passenger count at all, so this
   *  is a question only Helen can answer — the same one she answers today when
   *  she re-keys one of these by hand. No default is invented.
   *  ⭐ T1746 M5 (B7): it is now the ONLY pick on this screen. The home question
   *  it used to sit beside is gone, derived server-side from the request. */
  const [bandPick, setBandPick] = useState<Record<number, string>>({});
  /** What the agreed-price offer has said about each row (T1683 M1; the answer
   *  is the server's whole union since T1746 M5). */
  const [pricing, setPricing] = useState<Record<number, PriceLookup>>({});
  /**
   * ⛔⛔ WHICH ROW HAS THE AGREED-PRICE PANEL OPEN — AND IT IS MUTUALLY
   * EXCLUSIVE WITH `crossingId`, DELIBERATELY.
   *
   * ⚠️ T1746 M5 (B7) - THE REASON CHANGED AND THE RULE DID NOT. It used to be
   * that the panel held the HOME PICK, shared by both steps, and two pickers
   * would be two answers to one question. There is no pick now. What remains is
   * the reason `crossingId` already states on its own: two open steps would let
   * Helen read one row's evidence while pressing another row's button, and a
   * crossing reveal sitting open under an agreed-price answer for a DIFFERENT
   * row is exactly that shape.
   */
  const [priceHelpId, setPriceHelpId] = useState<number | null>(null);
  /**
   * ⭐⭐ T1749 M7 (R4, Q138's rider) — WHICH ROW IS OPEN, AND `undefined` IS A
   * THIRD STATE RATHER THAN A MISSING ONE.
   *
   * Alex's rider: at 1024 the ten-column table wrapped every cell to three or
   * four lines and "the text in the table does not look great and makes it hard
   * to read". R2's answer is density: a row carries SIX facts and everything else
   * sits behind one tap. So exactly one row is expanded at a time.
   * ⛔ `undefined` MEANS "NOBODY HAS CHOSEN YET" and the screen opens the first
   * row waiting on Helen; `null` means she CLOSED it. Without the third state,
   * closing the default row would be impossible (it would re-open on the next
   * render), and the alternative, defaulting to all-closed, puts an extra tap in
   * front of her main job every time.
   * ⛔ ONE AT A TIME IS ALSO WHAT KEEPS §5.2 TRUE. The open row carries the one
   * gold primary; a list that opened every priceable row would put a gold button
   * on each of them.
   */
  const [openId, setOpenId] = useState<number | null | undefined>(openRequestId);
  /**
   * ⭐⭐ T1749 M7 (C6 + DESIGN.md §5.1) — THE ENDING ACTIONS CONFIRM IN PLACE,
   * AND THAT IS WHERE THE CANCEL FACT WENT.
   *
   * The standing amber paragraph said, to every row at once, that cancelling here
   * only ends the REQUEST and a crossed run is still on the month's bill. C6
   * removes the paragraph and the fact moves to the moment it is about: the
   * confirm limb of the very tap that would do it.
   * ⛔ IT IS ALSO §5.1's RULE ARRIVING LATE. `Decline` and `Cancel` are
   * destructive and fired on the tap, with no confirm and no undo, on a screen
   * whose sibling destructive (`admin/DestinationList.tsx`) is the rulebook's
   * worked reference for confirm-in-place. Holding the row id AND the status
   * means one confirm is up at a time and it knows which act it is confirming.
   */
  /** ⭐ board 2812 M4 (step 0 (b)): an armed ask decline also holds the `askSeq` it was
   *  armed on, so a replaced ask (a new `askSeq` after a reload) is never declined with
   *  the reason picked for the old one: the row shows Decline again and must be re-armed. */
  const [endingAct, setEndingAct] = useState<{ requestId: number; status: string; askSeq?: number } | null>(null);
  /** ⭐ Board 2788 M6 (DD-5) - the decline reason picked per row. No default: an
   *  unpicked select keeps Decline off, so a reason nobody chose is never sent. */
  const [declineReason, setDeclineReason] = useState<Record<number, string>>({});
  const noticeRef = useRef<HTMLDivElement>(null);
  // Same latch as Operators': `busy` is fixed at render time and setState does
  // not re-render before a second tap in the same task, so a ref is the only
  // thing that actually closes the double-submit window. `busyId` still greys
  // the row out, which is a different job.
  const inFlight = useRef(false);
  /**
   * ⭐⭐ T1749 M7 (R4) — WHICH ROW OPENS WHEN NOBODY HAS CHOSEN: the first one
   * waiting on Helen, in this screen's own order.
   *
   * ⛔ DERIVED PER RENDER, NEVER PUT IN STATE. The list reloads after every
   * status write, so a remembered default would keep pointing at a row that has
   * just left the waiting set: the screen would open a request she has already
   * dealt with and leave the next one closed.
   * ⛔ AND IT READS `orderForHelen` RATHER THAN SCANNING FOR A STATUS, so the
   * order and the default cannot disagree. `null` when nothing is waiting, which
   * leaves every row closed and is the honest state of a cleared queue.
   * ⭐⭐ Board 2814 M2 (DD-11): it is a NEEDS-YOU-NOW ROW (`defaultOpenOf`: the
   * first with a gold, else the first), the list the count counts, so the screen
   * never opens on a Done row, a past row or a row that is a booking now. `now`
   * is read once per render and shared with the lists below.
   */
  const now = new Date();
  const lists = state.kind === 'ready' ? splitForHelen(state.requests, state, now) : null;
  const defaultOpenId = lists === null || state.kind !== 'ready' ? null : defaultOpenOf(lists.needs, state, now);

  /**
   * ⭐ Board 2814 M8R1 (R3) - THE LATEST READ WINS, NOT THE LAST TO ANSWER. A poll
   * reload and a reload after Helen's act can be in flight together; each takes a
   * number and only the newest may land, so an older answer never overwrites a
   * newer list.
   */
  const loadSeq = useRef(0);
  /** ⭐ board 2812 M4 (a home's To do) and M5's Keep current: the ONE rows read, so the
   *  mount load, the reload after Helen's act and 2814 M8R1's quiet poll reload all read
   *  the same list (one home's on a home's page, every home's otherwise). */
  const readRows = () =>
    (homeKey === undefined ? listPortalRequests({ notes: true }) : listHomeRequests(homeKey, { notes: true }));
  const load = () => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setState({ kind: 'loading' });
    // ⚠️ ONE READ NOW, NOT A `Promise.all` OF TWO (T1746 M5, B7). The second read
    // was the home list, and it existed for one consumer: the picker. Its "both
    // or neither" note said a screen that rendered rows behind an empty picker
    // would offer a crossing Helen could not complete - true, and moot once
    // there is nothing to pick. `getJson`, so an empty inbox stays a MEANING and
    // a broken seam stays an error.
    // ⭐⭐ board 2637 (D4): THIS screen's loads, and only these, ask for the notes.
    readRows()
      .then((list) => {
        if (seq !== loadSeq.current) return;
        setState({
          kind: 'ready',
          requests: list.requests,
          crossings: list.crossings,
          settlements: list.settlements,
          nurseNotes: list.nurseNotes,
        });
      })
      .catch((e: unknown) => {
        if (seq !== loadSeq.current) return;
        // ⭐ board 2812 M4 fix round (C2): one home's read answering 404 means the server
        // no longer knows the home; the host opens All homes instead of an error.
        if (homeKey !== undefined && e instanceof ApiError && e.status === 404 && onStale !== undefined) {
          onStale();
          return;
        }
        setState({
          kind: 'error',
          // No status codes and no server sentences reach this screen (SEC-5).
          message: 'Could not read the portal requests.',
        });
      });
  };
  useEffect(load, []);

  /**
   * ⭐⭐ Board 2814 M8R1 (R3) - THE ROWS RELOAD ON THE COUNT'S OWN POLL. App bumps
   * `pollTick` after each portal-count read it lands; this re-reads the rows
   * QUIETLY: no "loading" swap, so an open confirm step, a typed price, a typed
   * note, a picked band and an open confirm all stay as they are (they live in
   * this screen's own state, keyed by request id, which a reload does not touch).
   * ⛔ The row that is open stays open: when Helen has not chosen one, the one the
   * screen opened is pinned before the new list lands, so a request arriving above
   * it cannot swap the open card from under her typing.
   * ⛔ Skipped while one of her acts is in flight (that act reloads when it lands), and
   * while a load is pending (fix round 1, CR-1: see the effect). A failed quiet read
   * changes nothing on screen (the count shows its own "!").
   */
  const pollSeen = useRef(pollTick);
  const defaultOpenRef = useRef<number | null>(null);
  defaultOpenRef.current = defaultOpenId;
  useEffect(() => {
    if (pollTick === undefined || pollTick === pollSeen.current) return;
    pollSeen.current = pollTick;
    if (inFlight.current) return;
    // ⛔ Fix round 1 (CR-1): NOT while a load is still pending. Taking a newer number
    // here would retire that load's answer, and if this quiet read then failed (it
    // changes nothing on a failure) the screen would sit on "loading" until the next
    // good tick. The pending load lands on its own.
    if (state.kind === 'loading') return;
    loadSeq.current += 1;
    const seq = loadSeq.current;
    readRows()
      .then((list) => {
        if (seq !== loadSeq.current) return;
        const keep = defaultOpenRef.current;
        setOpenId((prev) =>
          (prev === undefined && keep !== null && list.requests.some((q) => q.requestId === keep) ? keep : prev));
        setState({
          kind: 'ready',
          requests: list.requests,
          crossings: list.crossings,
          settlements: list.settlements,
          nurseNotes: list.nurseNotes,
        });
      })
      .catch(() => undefined);
  }, [pollTick]);

  // Move the caret onto the announcement: the control that caused it is
  // disabled while in flight, so the browser has already blurred it to <body>.
  useEffect(() => {
    if (notice === null) return;
    noticeRef.current?.focus();
  }, [notice]);

  /**
   * ⭐⭐ T1749 M7 wave-3 (ux BLOCK 1, R11) - THE OPENED ROW SCROLLS TO THE TOP OF
   * THE PANE: the chrome above the list would otherwise push an open card's gold
   * primary below the ~700 plane (`e2e/tests/green/tablet-ia.spec.ts` SC-10d
   * measures it). `block: 'start'` and not `smooth`: a reduced-motion reader must
   * not be given an animation she did not ask for, and the jump is the point.
   *
   * ⭐⭐ Board 2647 (K1) - AND IT SCROLLS AFTER THE ROW ABOVE HAS COLLAPSED,
   * never before. The handle's click used to call `scrollIntoView` in
   * the same handler as `setOpenId`, so the pane scrolled while the previously
   * open card above was still tall; its collapse then pulled the clicked handle
   * up past the top of the pane, and the request Helen opened was not where the
   * scroll put it.
   * ⭐ The click PARKS the opened row's `bodyId` here (position-keyed, so it is
   * what identifies the handle after the commit) and this effect scrolls it.
   * `useLayoutEffect` runs synchronously at commit, after the DOM holds the new
   * open row and the collapsed one, so the pending-effect trap (an effect run at
   * the NEXT render) does not apply. Every open changes `openId` (undefined, null
   * or another id to this id), closing parks nothing, and a poll re-render leaves
   * `openId` unchanged, so the effect scrolls once per open and never on a poll.
   * ⚠️ FEATURE-DETECTED, NOT ASSUMED: jsdom has no `scrollIntoView`, so an
   * unguarded call is a TypeError in every spec that opens a row, and a screen
   * must not depend on a scroll for its correctness. The scroll is a courtesy;
   * the row opens either way.
   */
  const scrollOnOpenRef = useRef<string | null>(null);
  /**
   * ⭐ board 2707 M3 (DD4, ruling RB) - WHICH ROWS HELEN HAS EXPLICITLY OPENED
   * on this mount, so each tells the portal "seen" at most once. ⛔ ONLY THE
   * HANDLE'S CLICK writes here: the auto-open of the first waiting row
   * (`defaultOpenId`) is the screen's choice, not hers, and a false Seen tells a
   * care home it can stop ringing. A ref, not state: it renders nothing.
   */
  const seenMarked = useRef<Set<number>>(new Set());
  useLayoutEffect(() => {
    const bodyId = scrollOnOpenRef.current;
    scrollOnOpenRef.current = null;
    if (bodyId === null) return;
    const handle = document.querySelector(`button[aria-controls="${bodyId}"]`);
    if (handle instanceof HTMLElement && typeof handle.scrollIntoView === 'function') {
      handle.scrollIntoView({ block: 'start' });
    }
  }, [openId]);

  // -------------------------------------------------------------------------
  // T1683 M1 — the agreed account price, offered rather than typed from memory
  // T1746 M5 (B7) - asked for by REQUEST ID, with no home question at all
  // -------------------------------------------------------------------------

  const setLookup = (requestId: number, lookup: PriceLookup) =>
    setPricing((prev) => ({ ...prev, [requestId]: lookup }));

  /**
   * ⛔⛔ THE ROW'S IN-FLIGHT PRICE LOOKUP, SO A STALE ANSWER CANNOT LAND ON A
   * NEWER ASK.
   *
   * MEASURED, and it is a money error that looks like a rendering delay: hold
   * one row's response open, close and re-open the panel, then release the first
   * answer. Without the token the box ends up holding whichever read finished
   * LAST rather than the one the screen is currently asking.
   *
   * ⭐ THE HOUSE PATTERN, PORTED RATHER THAN REINVENTED. App.tsx's
   * `resolveCapturedPrice` effect wraps its lookup in
   * `let live = true; … return () => { live = false }` for exactly this reason.
   * A `useEffect` cleanup is not available here (the lookup hangs off an
   * `onClick`, not off rendered state), so the flag is held in a ref instead and
   * superseded by hand at each point the row's question changes.
   *
   * ⛔ KEYED PER ROW, NOT ONE FLAG FOR THE SCREEN. Every row resolves into its
   * own `pricing[requestId]`. A single latest-wins flag would discard a SIBLING
   * row's legitimate answer and strand that row reading "Checking the agreed
   * price…" for ever, because nothing re-asks.
   *
   * ⚠️ T1746 M5 (B7) - THERE IS ONE STORE NOW, NOT TWO. A second store held the
   * destination LIST's token, because the list and the figure answered different
   * questions ("which home?" and "which destination?") and so died at different
   * moments. Both questions are gone: the offer is one read keyed on the request,
   * so one token per row is the whole discipline. ⛔ If a second read is ever
   * added to this panel it needs its OWN store again - sharing one token makes
   * either read's ending kill the other, and nothing re-asks.
   */
  const priceLookup = useRef<Record<number, LiveToken>>({});

  /** End whatever read this row has in flight in `store`, and hand back the
   *  token the next one must still be holding when it answers. */
  function supersede(store: { current: Record<number, LiveToken> }, requestId: number): LiveToken {
    const previous = store.current[requestId];
    if (previous !== undefined) previous.live = false;
    const token = { live: true };
    store.current[requestId] = token;
    return token;
  }

  /**
   * ⭐⭐ THE ONE PLACE THAT ASKS FOR AN AGREED PRICE, AND IT ASKS BY REQUEST ID.
   *
   * ⛔⛔ NO ACCOUNT AND NO HOME GO OVER THE WIRE (T1746 M5, B7). The handler
   * derives both from the request's own key, so this screen cannot price one
   * home's card against another home's request - which is the defect #1720 S4
   * recorded when the operator's pick was the resolution.
   *
   * ⛔ EVERY GUARD AROUND THIS READ IS A MONEY GUARD and none of them may be
   * dropped: the live token taken BEFORE the read and re-checked in BOTH
   * continuations, the branch on `kind` and never on the number, and `failed`
   * being its own state rather than "no price on file".
   *
   * ⛔ THE ANSWER IS STORED WHOLE, NOT MAPPED. The server's union IS the
   * screen's state, so a new kind cannot be silently folded into an existing
   * sentence on its way in - `tsc` fails at the render instead, which is where
   * the operator-facing decision belongs.
   *
   * ⛔ THE TOKEN IS TAKEN BEFORE THE READ AND CHECKED AFTER IT. Both branches
   * return early when it is dead, including the failure one: a stale FAILURE
   * landing on a newer ask would replace a good figure with "could not check",
   * which is the same lie in the other direction.
   */
  function resolveAgreedPrice(requestId: number): void {
    const token = supersede(priceLookup, requestId);
    setLookup(requestId, { kind: 'checking' });
    void agreedPriceFor(requestId)
      .then((answer) => {
        if (!token.live) return;
        setLookup(requestId, answer);
      })
      // ⛔ A FAILED CHECK IS ITS OWN STATE, never "no price on file". One is
      // actionable and the other is not, and they used to render identically on
      // the Quote screen's money field.
      .catch(() => {
        if (!token.live) return;
        setLookup(requestId, { kind: 'failed' });
      });
  }

  /**
   * The figure this row is OFFERING, or undefined when there is nothing to
   * offer.
   *
   * ⭐⭐ THE OFFER AND THE BASIS SENTENCE ARE THE SAME `kind === 'priced'`
   * BRANCH, AND THAT IS THE WHOLE GUARD. A figure prefilled with no statement of
   * what it is a price FOR is how a one-way number gets sent as a return; here
   * the box cannot fill unless the sentence renders, because one condition
   * drives both. Pinned on the RENDERED TEXT rather than on a prop, so a basis
   * line that renders empty reds.
   *
   * ⛔⛔ AND THE CLAIM ABOVE WAS FALSE UNTIL THE LOOKUP BLOCK MOVED OUT OF THE
   * PANEL (T1683 M1 review, security F1). This function has only ever read
   * `kind`, but the sentence ALSO sat inside `panelOpen &&` — so any of three
   * ordinary acts stranded the figure with nothing beside it saying what it was
   * a price for. Measured, all three: pressing `Done`; pressing `Use the agreed
   * price` on a NEIGHBOURING row (the one-panel-at-a-time rule closes this one,
   * so an operator loses the explanation without touching this row at all); and
   * the sharp one, on a RETURN job - the box held a DOUBLED figure, nothing on
   * screen said one-way or return, and Save sent it.
   * ⚠️ THE WORD "latched" USED TO STAND HERE AND IT NAMED A DELETED CONTROL
   * (M1's operator tick). The HAZARD IS UNCHANGED - only its driver moved. It is
   * now the nurse's own answer off the seam, so the doubling happens with no
   * operator act at all and the stranded-figure case is if anything easier to
   * reach.
   *
   * ⛔ THE FIX IS THE RENDER SITE, NOT A SECOND CONDITION HERE. Gating the offer
   * on `panelOpen` too would have made the claim true by DELETING the feature:
   * the figure would vanish when she tidied the panel away, and on the
   * neighbouring-row case it would vanish from a row she never touched. So the
   * live region that carries the sentence is mounted OUTSIDE the panel, directly
   * above the euro box, on this same `kind` and nothing else. See its note at
   * the render site.
   */
  function offeredEuros(requestId: number): string | undefined {
    const lookup = pricing[requestId];
    if (lookup === undefined) return undefined;
    if (lookup.kind !== 'priced') return undefined;
    // ⛔⛔ THE ×2 IS THE SERVER'S AND THIS LINE MUST NEVER GROW ONE (T1746 M5,
    // S9). It used to read `oneWayCents * (r.returnTrip ? 2 : 1)`, the same
    // expression `Quote.tsx` carries, because the multiplier is sheet-scope and
    // nothing in `account_prices` records a return - so every consumer had to
    // apply it and every consumer was a place to forget it. The offer handler
    // now answers `totalCents` with the doubling already done, from the nurse's
    // own `returnTrip` re-read off the request rather than out of a browser.
    // ⛔ A `* 2` REINTRODUCED HERE WOULD NOT MERELY DUPLICATE THE RULE, it would
    // QUADRUPLE a return job's figure - and `portal-requests.spec.tsx` pins that
    // by answering a `totalCents` the fixture's own `oneWayCents * 2` does not
    // equal, so a client that computes reds instead of agreeing by luck.
    return (lookup.totalCents / 100).toFixed(2);
  }

  /**
   * ⭐⭐ T1683 M6 — WHETHER HELEN'S NEXT PRESS IS A **CONFIRMATION** RATHER THAN
   * AN ACT OF AUTHORSHIP, which is the whole shape of A3.
   *
   * Two conditions, and each one is doing work:
   *   · the lookup answered `priced` ⇒ there IS a figure on offer (never a
   *     truthiness test on the number — zero is a real agreed price);
   *   · she has typed NOTHING over it ⇒ the box still holds the resolved figure.
   *     `amountText` ranks typed above the offer, so without this the button
   *     would say "Confirm the agreed price" over digits she keyed herself.
   *
   * ⚠️ T1746 M5 (B7) - THERE WERE THREE, AND THE THIRD IS NOW THE SERVER'S. It
   * read "the row carries a KIOSK destination key", because only then was the
   * figure resolved for the journey the bus is actually doing rather than for a
   * destination Helen chose out of our catalogue. The offer route answers
   * `priced` ONLY when the request carried that key (anything else is
   * `no-destination`), so `kind === 'priced'` now IMPLIES the retired condition.
   * ⛔ DO NOT re-add a client-side key check to "be safe": it would be a second,
   * drifting statement of when a press is a confirmation.
   *
   * ⛔ IT DOES NOT CHANGE WHAT IS SENT — only what the control is called. The
   * write is the same `act(r, 'priced')`, reading the same `amountText`. A
   * SECOND button would be two answers to one question; a renamed one is the
   * same answer, said honestly.
   */
  function isConfirmation(requestId: number): boolean {
    if (amounts[requestId] !== undefined) return false;
    return offeredEuros(requestId) !== undefined;
  }

  /**
   * What the euro box holds, in precedence order: what Helen TYPED, then what
   * the agreed price OFFERS, then EMPTY.
   *
   * ⛔ ONE FUNCTION, READ BY THE INPUT AND BY `act` ALIKE. Two derivations would
   * let the box show one figure and the write send another, on a money field.
   *
   * ⛔⛔ THE THIRD RANK IS GONE, AND ITS ABSENCE IS A DECISION (T1746 M5, B7).
   * `r.pricedAmountCents` - the figure the portal already holds on the request -
   * used to prefill the box. It no longer does anywhere: not on `unpriced`, not
   * on `missing`, not on `unlinked`, not with the panel shut. That figure now
   * lives in the row's OWN Price column (`rowPriceText`), where it is a FACT
   * about the request rather than a proposed value for the next write.
   *
   * ⛔ WHY THE PREFILL HAD TO GO. It made two different questions look like one
   * answer. "No agreed price on their list, type the figure" is the whole point
   * of the `unpriced` and `missing` lines, and a box that arrived already
   * holding the last figure somebody saved invites Helen to press Save on a
   * number nobody just agreed - a re-affirmation dressed as a fresh decision.
   * The empty box is the honest ask, and it is what the offer route's own
   * sentences tell her to do.
   *
   * ⭐ AND IT RETIRES THE HALVED-LEGACY-RETURN HAZARD THIS BLOCK USED TO CARRY.
   * The recorded defect was: on a pre-M7 return job whose stored figure had been
   * doubled by M1's operator tick, opening the panel resolved a ONE WAY offer,
   * the offer outranked the stored figure, and the box HALVED. There is no
   * ranking left between them to get wrong - the stored figure never reaches
   * this box - and the offer is now the server's `totalCents`, doubled from the
   * nurse's own answer, so what the box shows and what the line says come from
   * one arithmetic in one place.
   *
   * ⚠️ WHAT IS STILL TRUE OF A LEGACY ROW: nothing anywhere records that a
   * pre-M7 job was a return, and recovering it from `stored === 2 * oneWay`
   * would be inferring an answer nobody gave, on money - the one thing this
   * screen may never do. Its Price column shows what the portal holds; its panel
   * offers what the home's card says today; the two may disagree and the screen
   * says both rather than picking.
   */
  function amountText(requestId: number): string {
    return amounts[requestId] ?? offeredEuros(requestId) ?? '';
  }

  async function act(r: PortalRequest, status: string): Promise<void> {
    if (inFlight.current) return;

    /**
     * ⛔⛔ WHAT THE ROW WAS ASKING WHEN THIS SAVE BEGAN - and the ONLY thing that
     * entitles the success path below to retire it.
     *
     * The retire step supersedes `priceLookup`, which is the SAME per-row store
     * `resolveAgreedPrice` takes its token from. `supersede` cannot tell a
     * stale answer from BEFORE this save apart from a lookup the operator
     * started DURING it, so an unconditional retire kills both - and nothing
     * disables `Use the agreed price` while a save is in flight, so she really
     * can re-ask mid-round-trip.
     *
     * ⛔ MEASURED, and it is the exact harm this milestone exists to prevent:
     * pick a priced destination, press Save, pick a DIFFERENT priced one before
     * the POST lands, and the box ends up EMPTY - no figure, no "Checking the
     * agreed price...", no error to prompt a retry. An agreed price is on file,
     * she picked it, and the screen shows nothing, so she hand-prices a run that
     * HAS a figure.
     *
     * ⇒ Capture the identity of both channels here and retire ONLY what is
     * still current when the write lands. A newer pick, or newer digits, mean
     * the operator has moved on and the success path must leave her alone.
     */
    const priceTokenAtStart = priceLookup.current[r.requestId];
    const typedAtStart = amounts[r.requestId];

    let pricedAmountCents: number | undefined;
    if (status === 'priced') {
      const cents = parseEuro(amountText(r.requestId));
      if (cents === null) {
        setAmountError({
          requestId: r.requestId,
          text: 'Enter the price as a whole amount above zero, like 125 or 125.50.',
        });
        return;
      }
      /**
       * ⛔⛔ A ZERO IS REFUSED, AND THE REFUSAL NAMES THE REAL REASON.
       *
       * A zero IS a price — a destination this home is carried to at no charge,
       * which is why the box prefills `0.00` and why the offer branches on
       * `kind` and never on the number. It simply cannot be SENT: the portal's
       * own column carries
       * `CHECK (priced_amount_cents IS NULL OR priced_amount_cents > 0)`
       * (portal/src/core/migrations.ts:149), so a zero cannot cross the seam at
       * all and relaxing that is a product decision, not a screen fix.
       *
       * ⛔ WHAT THE OLD SENTENCE DID. This case fell through the generic
       * "enter a whole amount above zero", which reads as "you typed it wrong"
       * and sends Helen to invent a figure for a run somebody agreed was free.
       * That is verbatim the harm `account-prices-repo.ts` names: reporting a
       * captured zero as unpriced "sends the job to the hand-priced fallback and
       * invents a charge nobody agreed".
       *
       * ⚠️ REACHABILITY, stated so the size of this is not overstated: no live
       * writer can mint a zero `account_prices` row today. Both
       * `createAccountPriceOn` and `setAccountPriceOn` go through
       * `usablePriceOrThrow`, which rejects `<= 0`, so a zero arrives only by
       * seed, import or manual SQL. It is pinned rather than latent because when
       * it does arrive it lands on a money field with no other warning.
       *
       * simplified: zero agreed prices cannot be sent, upgrade when the portal
       * CHECK at portal/src/core/migrations.ts:149 is relaxed OR a writer that
       * can mint a zero account_prices row appears - either makes this a
       * saveable value rather than an explained dead end.
       */
      if (cents === 0) {
        setAmountError({
          requestId: r.requestId,
          text: 'That agreed price is zero, and the portal cannot store a zero price. Do not invent a figure: leave this request unpriced and use the note to tell the home the run is at no charge.',
        });
        return;
      }
      pricedAmountCents = cents;
    }
    setAmountError(null);

    inFlight.current = true;
    setBusyId(r.requestId);
    setNotice(null);
    const typedNote = notes[r.requestId];
    try {
      const result = await setPortalRequestStatus(r.requestId, status, {
        ...(typedNote !== undefined ? { waitingNotes: typedNote } : {}),
        ...(pricedAmountCents !== undefined ? { pricedAmountCents } : {}),
        // ⭐ Board 2788 M4 (DD-1, S22): the revision THIS row rendered, so a
        // home change made after the list loaded is refused, never acted on.
        revision: r.revision,
      });
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message });
        // ⭐ Board 2788 M4 (DD-1, DD-18): a failed write reloads too, so a stale
        // row, a write that was not reached and one whose fate is unknown are all
        // shown as the portal now holds them.
        load();
        return;
      }
      // ⭐⭐ T2088 M8 — LEG B's OUTCOME IS SAID OUT LOUD, AND A REFUSAL DOWNGRADES
      // THE WHOLE NOTICE TO AN ERROR.
      //
      // ⛔ THIS IS THE PRODUCT HALF OF "both outcomes in the response". The wire
      // carried the booking outcome the moment leg B landed; until this line the
      // screen DISCARDED it, so a request whose linked run was REFUSED (on an
      // issued invoice, say) read as a plain green "Cancelled." — Helen believing
      // the bus is off while the home is still being billed for it. That is the
      // sharpest lie this screen can tell, and it is the one R-4 exists to remove.
      // ⚠️ `ok` ONLY WHEN THE RUN REALLY WENT. `changed: false` is an idempotent
      // repeat and still success; every refusal arm is an error, because the
      // request DID end and the run did NOT, which is a state she must act on.
      // ⭐ Board 2814 M4: the cash interim's Confirm (and its added sentence) is
      // gone; this route takes no `confirmed` for any payer now.
      setNotice(bookingNotice(r, status, result.value));
      load();
      // ⛔ TWO READS, NOT ONE. `load()` refreshes the ROWS on this screen;
      // `onChanged` refreshes the rail's COUNT. Dropping this leaves the badge
      // beside the row still counting a request the row itself now shows as
      // handled — the same screen disagreeing with itself.
      //
      // ⭐ DELIBERATELY ON THE SHARED SUCCESS PATH, below the `!result.ok`
      // early return, so it covers EVERY transition rather than the priced one
      // that happened to be the reproduction: declined, cancelled and confirmed
      // all take a row out of `new` and so all change the count. And a FAILED
      // write returns above this line, which is correct — nothing changed.
      onChanged();

      /**
       * ⛔ THE OFFER AND THE TYPED FIGURE RETIRE ON A SUCCESSFUL WRITE, so the
       * euro box re-derives from the SERVER like everything else on this screen
       * (T1683 M1 review, security F1, second half).
       *
       * `amountText` ranks typed above the offer above `r.pricedAmountCents`.
       * Without this the two upper ranks outlive the write that consumed them:
       * `load()` brings back the row the portal now holds and the box keeps
       * showing a figure from before it, so a later `Update price` would re-send
       * the pre-write number over whatever the row actually says. That is the
       * "RELOAD, NEVER PATCH" rule this file already states at `cross`, applied
       * to the one field where disagreeing with the server costs money.
       *
       * ⛔ THE SENTENCE RETIRES WITH THE FIGURE, and that is the same invariant
       * as `offeredEuros`, not a second one: both read `lookup.kind`, so an
       * offer cleared here cannot leave its basis line behind explaining a
       * figure that is no longer on offer.
       *
       * ⚠️ AND THE IN-FLIGHT LOOKUP DIES TOO. Blanking the state is not enough -
       * a resolve started before the write would land afterwards and repaint the
       * box, which is the same stale-figure-on-a-money-field shape the row's
       * token discipline exists for.
       *
       * ⭐ ON THE SHARED SUCCESS PATH for the reason stated just above: a
       * decline or a cancel ends the row's pricing too, and a per-status version
       * of this would be one more place to forget.
       */
      // ⛔ CONDITIONAL, per the note at the top of this function. `===` on the
      // token OBJECT, not on its `live` flag: a token superseded by a newer pick
      // is a different object, which is exactly the question being asked here.
      if (priceLookup.current[r.requestId] === priceTokenAtStart) {
        supersede(priceLookup, r.requestId);
        setLookup(r.requestId, EMPTY_LOOKUP);
      }
      // The two channels retire INDEPENDENTLY. She can key digits without
      // starting a lookup, and pick without typing, so one guard covering both
      // would let either act discard the other's work.
      //
      // ⛔ COMPARED INSIDE THE UPDATER, NOT AGAINST `amounts` OUT HERE. `amounts`
      // is STATE, so every read in this function - including after the await -
      // sees the snapshot this call closed over. `amounts[id] === typedAtStart`
      // written at this line is therefore ALWAYS TRUE and the guard is vacuous:
      // it would clear digits she keyed during the round trip while looking like
      // it checked. `prev` is the live value; `priceLookup` above needs no such
      // care because it is a REF.
      setAmounts((prev) => {
        if (prev[r.requestId] !== typedAtStart) return prev;
        const next = { ...prev };
        delete next[r.requestId];
        return next;
      });
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }

  /**
   * ⭐⭐ T2088 M8 — LEG A OF R-4. ONE CONTROL ON THE ROW CANCELS THE LINKED RUN.
   *
   * The story it exists for: a NURSE cancels a request in the portal, M6's email
   * reaches the office, and the bus is still on Helen's sheet. Before this, her
   * only route was to read the reference, leave this screen, find the booking and
   * cancel it there — which is Q108 option (b) done by hand, and the reason the
   * row carried a sentence telling her to do exactly that.
   *
   * ⛔ NO NEW ROUTE AND NO NEW HANDLER. It posts to the EXISTING
   * `POST /api/bookings/:id/cancel` through `cancelBookingRun`, the same action
   * `BookingDetail` has always used — so the deposit-link sweep, the
   * balance-invoice void and the `booking_events` attribution are the shipped
   * ones, and her session is the actor, which is what makes `cancelBooking`'s
   * hard-coded `actor_kind = 'operator'` truthful.
   * ⚠️ NO `refundCents` IS SENT. That relaxes the deposit-paid refusal and
   * commits the sender to refunding in the same action; this control has no
   * refund arm, so a deposit-paid run is refused by name and she opens the
   * booking. On a crossed row it is near-vacuous anyway (account-billed work has
   * no deposit stage) — which is a reason to say so, not to leave it unsaid.
   *
   * ⛔ IT RENDERS ON ANY CROSSED ROW WHOSE BOOKING IS STILL LIVE, WHATEVER THE
   * REQUEST'S STATUS — including a request that is already `cancelled` or
   * `declined`, which is the whole point: those rows have NO action buttons at
   * all (`ACTIONS` has no key for them), so without this there is no control.
   *
   * ⛔ THE SAME `inFlight` REF LATCH AS `act`, for the same measured reason:
   * `disabled` is fixed at render time and does not close the double-submit
   * window. The row-level `busyId` greys the row, which is a different job.
   * ⚠️ The server is idempotent underneath either way (`changed: false` on a
   * second call, and no second audit event), so a race costs an extra request,
   * never a second cancellation.
   *
   * ⛔ EVERY REFUSAL IS ANSWERED IN CLIENT-AUTHORED WORDS (SEC-5). The server's
   * sentence never reaches the screen; only `invoiceNumber` does, which is an
   * identifier this system allocated. The invoiced arm names the invoice AND the
   * remedy, because a refusal with no next step is how a crash becomes a shrug.
   */
  async function cancelRun(r: PortalRequest, bookingId: number, ref: string): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusyId(r.requestId);
    setNotice(null);
    try {
      const outcome = await cancelBookingRun(bookingId);
      if (outcome.kind === 'cancelled') {
        // ⭐⭐ T2088 REWORK 1 — AND WHETHER THE HOME WAS TOLD. Sweep 3 ends the
        // crossed portal request as part of this same cancel, so the ordinary
        // answer here is silence on that half; the two arms below are the ones
        // where the run is cancelled and the NURSE STILL READS "The taxi is
        // booked." ⛔ ON THIS SCREEN THE REMEDY IS DIFFERENT FROM `BookingDetail`'s,
        // which is why the copy is not the shared constant: Helen is already ON the
        // Requests tab, so "open the request on the Requests tab" would send her
        // where she is. The row's own `Cancel` control is the remedy, and it is one
        // press away.
        const told = outcome.portalRequest;
        setNotice(
          told === 'failed' || told === 'unreachable'
            ? {
                kind: 'error',
                text: `Booking ${ref} is cancelled, but the home has NOT been told: the nurse still sees this trip as booked. `
                  + (told === 'failed'
                    ? 'Cancel the request itself on this row, or ring the home.'
                    : 'Ring the home, and tell the office the portal link needs looking at.'),
              }
            : { kind: 'ok', text: `Booking ${ref} is cancelled.` },
        );
      } else if (outcome.kind === 'already-cancelled') {
        setNotice({ kind: 'ok', text: `Booking ${ref} was already cancelled.` });
      } else if (outcome.kind === 'refused-travelled') {
        // ⭐ Board 2814 M4 (Captain ruling (c)): this row never sends the
        // acknowledgement, so a stale row on a travelled run is refused, in the one
        // sentence every Requests-screen control gives.
        setNotice({ kind: 'error', text: `Booking ${ref}: ${runTravelledText('requests')}` });
      } else if (outcome.kind === 'refused-invoiced') {
        setNotice({
          kind: 'error',
          text: `Booking ${ref} is on invoice ${outcome.invoiceNumber}, which has already been issued. Void that invoice on the Invoices tab, cancel the run, then re-issue it.`,
        });
      } else if (outcome.kind === 'refused') {
        setNotice({
          kind: 'error',
          text: `Booking ${ref} cannot be cancelled from here. Open it and cancel it there, where a paid deposit can be refunded or retained.`,
        });
      } else {
        setNotice({ kind: 'error', text: `Booking ${ref} could not be cancelled. Try again, or open the booking.` });
      }
      // ⛔ RELOAD, NEVER PATCH — `act`'s own rule. The crossing's status is read
      // from the server, so the control retires because the row says so rather
      // than because this function assumed it would.
      load();
      // ⭐ board 2812 M3 fix round 2 (WT-2): ending a run moves the rail's count (row 12
      // leaves Need you now) and a hosting To do's piles, as every other write here does.
      onChanged();
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }

  /**
   * ⭐⭐ Board 2814 M4 (plan DD-11 rows 7, 8 and 14) - HELEN SETTLES AN OLD UNBOOKED
   * ROW BY HAND ("Booked another way", "Not going ahead"), or undoes it. No confirm
   * step: Undo puts it back, and nothing reaches the portal (the home's card is
   * unchanged; if the home must be told, she uses the row's Cancel). The server
   * re-derives rows 7 and 8 itself before it writes. `act`'s latch, reload and
   * count refresh, for its reasons.
   */
  async function settleRow(r: PortalRequest, reason: PortalSettlement['reason'] | null): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusyId(r.requestId);
    setNotice(null);
    try {
      const result = reason === null
        ? await undoPortalRequestSettlement(r.requestId)
        : await settlePortalRequest(r.requestId, reason);
      setNotice(
        !result.ok
          ? { kind: 'error', text: result.message }
          : reason === null
            ? { kind: 'ok', text: `${r.homeName}: back in Needs you now.` }
            : {
              kind: 'ok',
              // ⭐ board 2814 M8R1 (R10): settling writes nothing to the portal, so a
              // Confirmed request the home still reads as booked says so, and who to tell.
              text: `${r.homeName}: settled by hand, ${SETTLE_REASON_WORDS[reason].toLowerCase()}. It is in Done.`
                + (reason === 'not_going_ahead' && r.status === 'confirmed'
                  ? ' The home still sees this trip as Confirmed. Tell them.'
                  : ''),
            },
      );
      load();
      onChanged();
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }

  /**
   * ⛔⛔ THE DECISION IS GUARDED IN THE HANDLER, NOT ONLY BY `disabled`.
   *
   * `disabled` stopped NOTHING when a grading spec dispatched a click straight
   * at the element — measured in this repo, not theorised — and the same is true
   * of a script and of a stale render. So the preconditions are re-checked here,
   * where a dispatched event cannot walk past them.
   *
   * ⚠️ AND THE CHECK THAT IS **NOT** HERE IS THE INTERESTING ONE. "Is the reveal
   * open on this row" is deliberately NOT re-tested: the confirm button only
   * exists while it is open, and the confirm CLEARS the reveal before firing
   * (the house idiom), so such a test would be either vacuous or self-defeating.
   * What is genuinely reachable without a guard is a confirm pressed with NO
   * home and NO band chosen — those controls start empty by design — which would
   * otherwise POST `undefined` for both. Those three lines are the guard, and
   * they are the ones a mutation test can kill.
   *
   * ⛔ THE SERVER RE-CHECKS EVERY ONE OF THEM ANYWAY, because the browser is not
   * the authority for a write that attributes money to a nursing home's account.
   */
  /**
   * ⛔ A REFUSED CONFIRM SAYS WHY, AND RE-OPENS THE STEP IT REFUSED
   * (T1447 M7 review, F8). It used to `return` silently: the reveal closed —
   * the confirm's own onClick clears it BEFORE firing, which is the house
   * idiom — and then nothing happened, no request, no message, no sign that a
   * press had landed at all. Safe, and completely opaque.
   *
   * ⛔ RE-OPENING IS HALF THE FIX, NOT A FLOURISH. The reason names a control
   * ("choose which home"), and a reason pointing at a control that is no longer
   * on screen is not actionable. `kind: 'error'` is what renders the shared
   * Banner as `role="alert"` (components/Banner.tsx), and the existing focus
   * effect moves the caret to it — so this is the screen's own idiom, not a new
   * channel.
   */
  function refuseCrossing(r: PortalRequest, text: string): void {
    setNotice({ kind: 'error', text });
    setCrossingId(r.requestId);
  }

  /**
   * ⚠️ Board 2814 M1 - `crossingConfirmLabel` STOOD HERE AND IS RETIRED. It gave
   * the old step's confirm the accessible name `Yes, make a booking for <home>`,
   * so a screen-reader operator heard which home would be BILLED (T1447 M7 F7).
   * The confirm step now SAYS that before the press ("Billed to the home's
   * account", over the row that names the home), and its button's name is its
   * own text, `Yes, confirm and book it`, which the e2e arm in
   * `e2e/tests/green/tablet-ia.spec.ts` finds by that exact name.
   */

  /**
   * ⭐⭐ Board 2814 M1 (plan DD-2, D10) - ONE PRESS BOOKS IT AND CONFIRMS IT.
   *
   * The same press completes three shapes: a Priced request (book, then mark),
   * a Confirmed request with no booking (book; no mark, so no note), and a
   * "landed, no booking" row (book the landing). A Priced row whose booking was
   * made but whose mark failed takes it too: the server's settled replay only
   * re-tries the mark.
   * ⛔ THE STEP STAYS OPEN WHILE THE WRITE IS IN FLIGHT, and that is a change
   * from the old reveal's "clear, then fire": the duplicate answer (DD-4) has to
   * render INSIDE the step with "Book it anyway", so the step closes on every
   * other outcome instead. The `inFlight` latch and the disabled buttons are what
   * keep a two-second write from being pressed twice.
   * ⛔ THE LIST RELOADS AFTER EVERY OUTCOME, success or not (RELOAD, NEVER PATCH).
   */
  async function cross(r: PortalRequest, opts: { bookAnyway: boolean }): Promise<void> {
    if (inFlight.current) return;
    const band = bandPick[r.requestId];
    // ⛔ THE BAND GUARD STAYS EXACTLY AS IT WAS, and it is still the reachable
    // case: the band picker starts empty by design, `disabled` stops NOTHING when
    // a click is dispatched at the element (measured in this repo), and without
    // this the screen would POST `undefined` for the one answer only Helen can give.
    if (band === undefined || band === '') return refuseCrossing(r, 'Choose how many are travelling, then press again.');
    // ⛔⛔ T1746 M3 (item 2a) - IT REFUSES WHEN THE LINK IS UNKNOWN: with
    // `crossings === null` this screen cannot tell a request that has already
    // crossed from one that has not, and this press makes a booking.
    if (state.kind === 'ready' && state.crossings === null) {
      setCrossingId(null);
      setNotice({ kind: 'error', text: CROSSINGS_UNKNOWN_LINE });
      return;
    }
    // ⛔ AND IN THE HANDLER, not only in the render: a stale screen or a
    // dispatched click still holds the control, and the server is the third
    // layer, not the first.
    if (state.kind !== 'ready' || confirmKindOf(r, state, new Date()) !== 'step') return;
    const crossing = state.crossings === null ? undefined : state.crossings[r.requestId];
    // ⭐ The note rides a PRICED request's mark only: a confirmed one gets no
    // mark, so the step said no note is sent, and none is. ⛔ AND ONLY A NOTE
    // SHE TYPED travels, `act()`'s own rule: the request's existing note is
    // already the home's, and re-sending what she never touched is a write she
    // did not make.
    const typedNote = notes[r.requestId];
    const note = r.status === 'priced' && typedNote !== undefined ? typedNote : '';

    inFlight.current = true;
    setBusyId(r.requestId);
    setNotice(null);
    try {
      const result = await convertPortalRequestToBooking(r.requestId, {
        passengersBand: band,
        notes: note,
        // ⭐ Board 2788 M4 (DD-1, S22): the revision this row rendered.
        revision: r.revision,
        ...(opts.bookAnyway ? { bookAnyway: true } : {}),
      });
      if (!result.ok) {
        if (result.duplicates !== undefined) {
          // ⭐ DD-4: the answer lives in the step, which stays open on this row.
          setDuplicate({ requestId: r.requestId, matches: result.duplicates });
          setCrossingId(r.requestId);
        } else {
          setDuplicate(null);
          setCrossingId(null);
          setNotice({ kind: 'error', text: result.message });
        }
        load();
        return;
      }
      setDuplicate(null);
      setCrossingId(null);
      // ⭐ DD-1.5: a home that cancelled inside the window leaves a cancelled row
      // with a live booking, whose own "End the run" is the control. The row is
      // held open so that control is where she is looking.
      if (result.value.requestMarked === 'home_cancelled') setOpenId(r.requestId);
      setNotice(crossingOutcomeNotice(r, crossing, result.value));
      load();
      onChanged();
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }

  /**
   * ⭐⭐ Board 2788 M6 (step 2; M4's routes, DD-1, DD-18) - HELEN ANSWERS THE HOME'S
   * ASK, posting the `askSeq` and the `revision` THIS ROW RENDERED, so a home change
   * made after the list loaded is refused rather than answered unseen. The same
   * `inFlight` latch as `act`, for its measured reason. ⛔ THE LIST RELOADS AFTER
   * EVERY OUTCOME, including `not_reached` and `unknown` (DD-18), and the count is
   * refreshed with it.
   */
  async function answerAsk(r: PortalRequest, decision: 'accept' | 'decline'): Promise<void> {
    if (inFlight.current) return;
    if (r.askState !== 'open' || typeof r.askSeq !== 'number' || typeof r.revision !== 'number') return;
    const reason = DECLINE_REASON_WORDS.find((d) => d.value === declineReason[r.requestId])?.value;
    // ⛔ In the handler too, not only by `disabled`: a dispatched click walks past
    // a disabled control (measured in this repo; see `cross`).
    if (decision === 'decline' && reason === undefined) {
      setNotice({ kind: 'error', text: 'Pick why you are saying no, then press Decline again.' });
      return;
    }
    inFlight.current = true;
    setBusyId(r.requestId);
    setNotice(null);
    try {
      const outcome = decision === 'accept'
        ? await acceptPortalAsk(r.requestId, { askSeq: r.askSeq, revision: r.revision })
        : await declinePortalAsk(r.requestId, { askSeq: r.askSeq, revision: r.revision, reason: reason! });
      setNotice(askNotice(r, decision, outcome));
      setDeclineReason((prev) => {
        const next = { ...prev };
        delete next[r.requestId];
        return next;
      });
      load();
      onChanged();
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }

  /** The row's "Note for the home" as the field shows it: typed, else the
   *  request's own. The ONE reading, for the field, the step and the press. */
  function noteText(r: PortalRequest): string {
    return notes[r.requestId] ?? r.waitingNotes ?? '';
  }

  /** The Done list, set while `body` below is built when a host arranges it (UX-8). */
  let doneNode: ComponentChildren = null;
  const body = (
    // ⭐ T939 M5 — DEMOTED FROM A SCREEN TO A SECTION, and the wrapper went with
    // the demotion rather than being dropped by accident.
    //
    // This was its own rail destination (T928) and carried the `screen-pane
    // screen-pane--wide` wrapper T933 F1 added, plus an <h1>. Alex's control-
    // centre ruling folds it into `Nursing homes`, which now owns BOTH: the
    // pane wrapper (still `--wide`, because `.portal-table` at min-width 62rem
    // is the widest thing in the kiosk and a narrower pane only lengthens its
    // side-scroll) and the page's single <h1>. A second pane here would nest
    // padding inside padding, and a second <h1> would give the screen two top
    // headings and steal App.tsx's focus-to-title target — that hook queries
    // `.screen-title` and takes the FIRST match.
    //
    // ⚠️ The heading STAYS, as an <h2>. The section still has to name itself:
    // the rail row now reads "Nursing homes", so without this line nothing on
    // screen says these rows are the REQUESTS. `role="heading"` is unchanged,
    // so every query that addressed it by name still resolves.
    <>
      {/* ⭐⭐ T1749 M7 wave-3 (ux) — THE `Nursing home requests` HEADING IS GONE.
          It repeated the tab that had just been pressed, on a screen whose `h1`
          already says `Nursing homes`, and it cost about 40px of the ~700 plane
          that R11 measures the gold primary against (impeccable P1-2). Nothing
          names this list twice now.
          ⚠️ ITS `tabIndex={-1}` WAS NOT A FOCUS TARGET ANYTHING MOVED TO: the
          screen's focus target is the `h1` the shell moves the caret to, which is
          unchanged. Re-derive before re-adding one:
          `/usr/bin/grep -rn 'screen-sub' quote-helper/src/client/screens/admin/NursingHomes.tsx`. */}

      {/*
        ⛔⛔ THE TWO STANDING AMBER PARAGRAPHS ARE GONE (T1749 M7, C6; Alex,
        2026-09-11, ask 890): "we need to get rid of this text, it gives this
        screen a cluttered view". Neither fact is dropped: both MOVE to where
        they are about something.

        · THE ATTRIBUTION NOTE (B3: anyone signed in to this kiosk can price,
          decline or cancel, and the portal records the act as the business
          rather than as her by name) is a fact about WHO CAN SIGN IN, not about
          any request on this list. It now sits once on `admin/Operators.tsx`,
          the screen that manages exactly that, beside the push-alert line.
        · THE CANCEL WARNING (ending a request here ends only the REQUEST; a
          crossed run is still going ahead and still on the month's bill) is a
          fact about ONE TAP. It renders inside the confirm limb of that tap, per
          row, where §5.1's confirm-in-place rule already wanted it and where the
          booking reference can be named.

        ⛔ SO THE SCREEN SAYS LESS AND THE OPERATOR IS TOLD MORE: a paragraph
        every row had to be read past became a sentence at the decision. The arms
        that graded the standing paragraphs are re-pointed in
        `portal-requests.spec.tsx` rather than deleted: one asserts no standing
        `p.field-hint` survives on this tab, the other asserts the cancel fact
        appears in the confirm step.
      */}

      {notice && (
        <Banner kind={notice.kind === 'ok' ? 'ok' : 'error'} elementRef={noticeRef} tabIndex={-1}>
          {notice.text}
          {/* ⭐ Board 2814 M1 (DD-2) - the booking the press made, one tap away,
              through the same navigation the row's booking line uses. */}
          {notice.openBookingId !== undefined && (
            <>
              {' '}
              <Button
                variant="quiet"
                size="inline"
                onClick={() => onOpenBooking(notice.openBookingId as number)}
              >
                Open the booking
              </Button>
            </>
          )}
        </Banner>
      )}

      {amountError && (
        <Banner kind="error" id={AMOUNT_ERROR_ID}>
          {amountError.text}
        </Banner>
      )}

      {/* ⭐ role="status" is KEPT, and it is a deliberate divergence from the
          kiosk's own habit: `.helper-note` appears at ten sites in this app and
          none of them carry it. Dropping it here to "match" would be a straight
          accessibility regression on a screen whose whole content arrives
          asynchronously - a screen-reader user would hear nothing at all while
          it loaded. Recorded as the one intentional inconsistency (M5). */}
      {state.kind === 'loading' && (
        <p class="helper-note" role="status">
          Getting the requests…
        </p>
      )}

      {state.kind === 'error' && (
        <Banner kind="error">
          {state.message}{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={load}>
            Try again
          </button>
        </Banner>
      )}

      {/* ⭐⭐ T1749 M7 rework 1 — THE LIBRARY'S EmptyState, not a bare
          paragraph. §7.1 gives a list's placeholder a headline and `role="status"`
          so the sentence is ANNOUNCED when it replaces rows under the reader; a
          `<p>` changed silently. `EmptyState` decides no action, which is why the
          `action` prop is absent here: there is nothing Helen does on this screen
          to make a home request exist. */}
      {state.kind === 'ready' && state.requests.length === 0 && (
        <EmptyState headline="No requests from the homes right now." />
      )}

      {/* ⭐⭐ T1749 M7 (R4 + Q138's rider) — THE TEN-COLUMN TABLE IS A `Card` ROW
          LIST, and the scroll wrapper went with it.
          ⛔ THE WRAPPER WAS LOAD-BEARING WHILE THERE WAS A TABLE: a `<table>` is
          its own formatting context and grows past its parent, so `overflow-x`
          had to live on an element it sat inside, and without it the ten columns
          either overlapped or ran off the right of the kiosk with no way back.
          There is no table here now: a list of cards wraps, so nothing scrolls
          sideways and the keyboard-reachable scroll region it needed is not a
          region any more.
          ⛔ AND THAT IS THE ANSWER TO §8's OWNER QUESTION, which read "Owner: M7
          decides whether it stays a table or becomes rows": it becomes rows, so
          the sideways scroll on the screen Helen hits daily is GONE rather than
          framed. The surfaces that stay tables (the account book's Bookings tab,
          Nursing homes) are framed per Q138 and keep their wrapper. */}
      {/* ⭐⭐ Board 2814 M2 (plan DD-11, M2 step 3) - TWO LISTS: NEEDS YOU NOW
          FIRST, THEN A QUIET DONE LIST, each in `orderForHelen`'s order. A request
          that is a booking now (DD-11 row 11) is in neither. ⛔ ONE ROW RENDERER
          FOR BOTH, and the row index runs ON across the two lists, so every
          position-keyed id stays unique on the screen. */}
      {state.kind === 'ready' && state.requests.length > 0 && lists !== null && (() => {
            const renderRow = (r: PortalRequest, rowIndex: number) => {
              const hasAmountError = amountError?.requestId === r.requestId;
              const price = pricing[r.requestId] ?? EMPTY_LOOKUP;
              /**
               * ⚠️ Board 2814 M1 - THE PANEL IS THE AGREED-PRICE HELP ALONE NOW.
               * It held the old crossing step too, because both once shared the
               * home pick; the confirm step swaps in place of Confirm instead
               * (DD-2), so `crossingId` no longer opens this region.
               */
              const panelOpen = priceHelpId === r.requestId;
              /**
               * ⭐ T1746 M5 (ux review, WARN 2 + WARN 3) - TWO PER-ROW IDS, and
               * they name TWO DIFFERENT THINGS on purpose.
               *
               * `panelId` is the collapsible region the toggle controls;
               * `offerId` is the live region holding the agreed-price answer,
               * which lives OUTSIDE the panel and must keep living there (T1683
               * M1 review, security F1: the sentence and the figure it explains
               * are one condition, and gating the sentence on the panel stranded
               * the figure). ⇒ the toggle points at `panelId`; the euro box
               * points at `offerId`. Pointing either at the other would tell a
               * screen-reader user that the answer is inside a region she just
               * collapsed.
               */
              /* ⛔⛔ EVERY ID ON THIS ROW IS KEYED BY THE ROW'S POSITION, NEVER BY
                 ITS `requestId` (T1749 M7 rework 1, the M7 adoption criterion:
                 "no field id is built from wire data"). A wire id in the DOM is
                 two defects in one: it publishes a database key into a surface
                 the copy gates do not read and the feedback recorder does, and it
                 makes the id's UNIQUENESS a property of the server's answer
                 rather than of this render. A duplicate `requestId` on the wire
                 would silently give two boxes one id, and `aria-describedby` then
                 resolves to whichever came first.
                 ⚠️ A CONSTANT id WOULD NOT DO, and the reason is measured rather
                 than assumed: every row renders its open body and only HIDES it
                 (`hidden={!rowOpen}` below), so all of them mount their price,
                 note and band fields at once. The per-screen duplicate-id arm in
                 `portal-requests.spec.tsx` is what holds this. */
              const rowKey = String(rowIndex);
              const panelId = `portal-panel-${rowKey}`;
              const offerId = `portal-offer-${rowKey}`;
              /** ⭐ T1746 M1 (B4). `undefined` means this request has NOT
               *  crossed; a PRESENT entry with a null `bookingId` means it
               *  landed and the booking was refused, which is a third state and
               *  reads differently below.
               *  ⛔ T1746 M3 (item 2a): `crossingsUnknown` is a FOURTH state and
               *  it outranks all three. It is not "no crossing" - it is "this
               *  screen does not know", so `crossing` is left undefined AND
               *  every control below is withheld, rather than falling through to
               *  the uncrossed rendering, which would assert the one thing the
               *  degraded route cannot support. */
              const crossingsUnknown = state.crossings === null;
              const nurseNote: string | undefined = state.nurseNotes[r.requestId];
              const crossing = state.crossings === null ? undefined : state.crossings[r.requestId];
              /**
               * ⭐⭐ T1749 M7 (R4) — IS THIS THE OPEN ROW? `openId === undefined`
               * is "nobody has chosen", and the screen opens the FIRST row that is
               * waiting on Helen, so her main job needs no extra tap. `null` is
               * "she closed it", which is why the three states are not two.
               */
              const rowOpen = suppressOpen === true
                ? false
                : openId === undefined ? r.requestId === defaultOpenId : openId === r.requestId;
              /* ⭐ T1749 M7 rework 1 — the row's action list, read ONCE, and the
                 one entry that writes the PRICE pulled out of it so it can render
                 beside the price box rather than after both fields. Everything
                 else keeps the action row and the separator law. */
              // ⭐ Board 2788 M6: while the home's CANCEL ask is open, the row's own
              // `Cancel` steps aside. Accepting the ask is that act, done as an answer
              // (the ask reads accepted); a second control for it would be two buttons
              // for one decision, and its name is a substring of the accept's.
              const rowActions = (Object.hasOwn(ACTIONS, r.status) ? ACTIONS[r.status]! : [])
                .filter((a) => !(a.status === 'cancelled' && r.askState === 'open' && r.askKind === 'cancel'));
              /* ⛔ GATED ON THE FIELD EXISTING, or the control would be filtered
                 out of the action row and rendered nowhere: only a PRICEABLE
                 status draws the price box, and a status that gains a `priced`
                 action without one must keep its button where it always was. */
              const priceAction = PRICEABLE.has(r.status)
                ? rowActions.find((a) => a.status === 'priced')
                : undefined;
              /* ⭐⭐ Board 2814 M1 (DD-2, DD-3, D1, D2, D10, D11) — WHAT THE ROW'S
                 CONFIRM IS, from `confirmKindOf` (the handler reads the same
                 function). A Confirmed row with no booking gains the control as
                 its gold primary, ahead of its Cancel; a row with no Confirm
                 keeps every other action. */
              const confirmKind = confirmKindOf(r, state, now);
              /* ⭐ Board 2814 M8R1 (R4): while this row's confirm step is open, its
                 OTHER controls are off (price, agreed price, note, Decline, Cancel),
                 so the step's recap and note cannot change under the press. "Not now"
                 turns them back on. */
              const stepOpen = confirmKind === 'step' && crossingId === r.requestId && state.crossings !== null;
              // ⭐⭐ Board 2814 M2 (DD-11) - the row's place, primary, gold and words.
              const where = placeOf(r, state, now);
              const listedActions = rowActions.filter(
                (a) => a !== priceAction && (a.status !== 'confirmed' || confirmKind !== 'none'),
              );
              const actionList = r.status === 'confirmed' && confirmKind === 'step'
                ? [{ status: 'confirmed', label: 'Confirm' }, ...listedActions]
                : listedActions;
              // ⭐ board 2812 M3: the shared Row names its body `<id>-body`. A POSITION,
              // never the request id: the click recorder writes it (Row's docstring).
              const rowId = `portal-row-${rowKey}`;
              const bodyId = `${rowId}-body`;
              // ⭐ board 2707 (M1, R-2): when the home asked, once per row; null
              // (unparseable, absent) omits the open card's `Requested` fact.
              const requestedAt = formatDublinInstant(r.createdAt);
              // ⭐⭐ Board 2788 M6 (step 2) - the row's change state, read once.
              const changeMark = changeMarkOf(r);
              const askChip = changeMark !== null && r.askState === 'open';
              const askLine = askLineOf(r);
              const cancelAsked = r.askState === 'open' && r.askKind === 'cancel';
              /** ⭐ board 2812 fix round (item 6): the home asked to CHANGE this trip, so the
               *  card's two red controls are named for what each one ends. */
              const changeAsked = r.askState === 'open' && r.askKind === 'change';
              const changedPriced = r.homeChanged === true && (r.status === 'new' || r.status === 'priced');
              const mismatch = bookingRequestMismatch(r, crossing);
              const historyLines = historyLinesOf(r);
              const linkedRun = crossing !== undefined && crossing.bookingId !== null
                ? {
                  ref: bookingRef(crossing),
                  live: isBookingCancellable(crossing.status),
                  travelled: crossing.travelled === true,
                  keepsOpen: travelledRunKeepsRequest(crossing.status, crossing.travelled),
                }
                : null;
              const askCancelArmed = endingAct !== null && endingAct.requestId === r.requestId && endingAct.status === ASK_CANCEL_ACT;
              const askDeclineArmed = endingAct !== null && endingAct.requestId === r.requestId && endingAct.status === ASK_DECLINE_ACT
                && endingAct.askSeq === r.askSeq;
              // ⭐⭐ Board 2814 M8R1 (Captain's ruling on Q287): a linked run that has
              // travelled and is not cancelled keeps its request open (the server's own
              // predicate, `travelledRunKeepsRequest`; fix round 1, CR-2), so every confirm
              // that would close it (Cancel, Decline, accepting a cancel ask) says why and
              // offers no "Yes". The server refuses the same press (`request_run_travelled`).
              const runTravelled = linkedRun !== null && linkedRun.keepsOpen;
              const repeating = r.journeyDate === null;
              return (
              /* ⭐⭐ board 2812 M3 (plan step 2) — THE CARD HEAD IS THE SHARED `Row`. One tap
                 opens this row and closes whichever was open; the Row announces
                 `aria-expanded`, names its body in `aria-controls`, carries
                 `data-fb-opaque` (the head's text is the resident's name) and draws the
                 ONE chevron cue. When, who (the resident, and the accessible-vehicle
                 fact beside them, ADR2-D15), where (the home, the home's own number
                 behind its marker, the destination, the appointment), the price, and
                 ONE chip: a home's ask takes it ("Change asked", "Cancel asked"),
                 else the status pill. The handle's accessible name still ENDS with
                 that word, which is how tablet-ia SC-10d and SC-10e find a row.
                 ⚠️ THE BODY MOUNTS ONLY WHILE OPEN (it used to render hidden), so the
                 per-row ids below are unique by position and by construction. */
              <Row
                key={r.requestId}
                id={rowId}
                opens="in-place"
                expanded={rowOpen}
                onOpen={() => {
                  // ⭐ Board 2647 (K1): an OPEN parks this row's `bodyId` and the
                  // `useLayoutEffect` on `openId` scrolls it after the commit; a
                  // close parks nothing.
                  if (!rowOpen) scrollOnOpenRef.current = bodyId;
                  // ⭐ board 2707 M3 - an EXPLICIT open is Seen, once per row per
                  // mount, never awaited (see `seenMarked`).
                  if (!rowOpen && !seenMarked.current.has(r.requestId)) {
                    seenMarked.current.add(r.requestId);
                    markPortalRequestSeen(r.requestId);
                  }
                  // ⭐ board 2812 M3: the host To do closes its own open card.
                  if (!rowOpen) onOpenRow?.();
                  setOpenId(rowOpen ? null : r.requestId);
                }}
                /* The lead time alone in the when slot (its column is narrow at 1024);
                   the return time rides the where line, after the destination it is
                   the return FROM, so the head stays inside R11's ~700 plane. */
                /* ⭐⭐ board 2812 M3 fix round 2 (UX-8 / PA-6): a Done row is QUIET and
                   compact: its day and time at body size, no time meaning; Need you now
                   keeps the emphasis tier (board 2637 D1). */
                when={where.place === 'done'
                  ? <span>{whenText(r, false)}</span>
                  : <span class="portal-request-when">{whenText(r)}</span>}
                who={where.place === 'done'
                  /* ⭐⭐ board 2812 M3 fix round 2 (UX-8 / PA-6): a Done row is ONE line: the
                     resident and the home on the who line and no where line (its destination
                     is behind the tap, in the facts). ⚠️ Measured on the seat at 1024: a long
                     "resident · home" still wraps in the who column (78px against a short
                     Coming up row's 58px); one line at 1440 (78 = 78). The home stays on the
                     head because every reader finds a request row by its home. */
                  ? (
                    <span class="portal-request-subject">
                      {/* ⭐ board 2812 M4: on a home's page the row drops the home's name. */}
                      {homeKey === undefined
                        ? <span>{r.residentDisplayName}{' \u00b7 '}<span class="portal-request-home">{r.homeName}</span></span>
                        : <span>{r.residentDisplayName}</span>}
                    </span>
                  )
                  : (
                    <span class="portal-request-subject">
                      {r.residentDisplayName}
                      {r.accessibleVehicle && <span class="needs-accessible-vehicle">Accessible vehicle</span>}
                    </span>
                  )}
                where={where.place === 'done' ? undefined : (() => {
                  /* Each fact keeps its own element, so a row is still found by its home
                     and its destination reads as one unit.
                     ⭐ board 2812 M3 fix round 2 (UX-7): the separator goes only BETWEEN
                     facts that are there, so no line ends "Hospital ·" and none starts
                     "· Changed"; the appointment is its own caption line (a block), so it
                     takes a space, never a dot. "Changed" is not an ask, so it is the
                     row's NOTE, not its chip. */
                  const phone = fallbackPhoneFor(homes, r.homeName);
                  const facts: VNode[] = [
                    // ⭐ board 2812 M4: on a home's page the row drops the home's name.
                    ...(homeKey === undefined ? [<span key="home" class="portal-request-home">{r.homeName}</span>] : []),
                    ...((r.destinationName ?? '') !== '' || r.returnTrip
                      ? [(
                        <span key="dest" class="portal-request-destination">
                          {r.destinationName}
                          {r.returnTrip && <span class="price-basis"> (and back)</span>}
                        </span>
                      )]
                      : []),
                    ...(changeMark !== null && !askChip ? [<span key="changed">{changeMark}</span>] : []),
                  ];
                  /* ⭐ board 2812 M4 fix round (C8): the home's phone rides the FIRST PRESENT
                     fact, and stands alone when there is none: on a home's page the home
                     name fact is gone, so a request with no destination and no change mark
                     would otherwise lose "Ring <home phone>" altogether. */
                  const ring = phone === null ? null : <span class="return-back" data-fb-opaque>Ring {phone}</span>;
                  return (
                    <>
                      {facts.map((f, i) => (i === 0
                        ? <Fragment key={f.key ?? i}>{f}{ring}</Fragment>
                        : <Fragment key={f.key ?? i}>{' \u00b7 '}{f}</Fragment>))}
                      {facts.length === 0 && ring}
                      {/* Back and the appointment are block lines of their own (`.return-back`,
                          `.portal-request-appointment`), so each follows a space, never a dot
                          that would end the line above ("Hospital (and back) ·"). */}
                      {backText(r) !== null && <>{' '}<span class="return-back">Back: {backText(r)}</span></>}
                      {appointmentText(r) !== null && (
                        <>{' '}<span class="portal-request-appointment">{appointmentText(r)}</span></>
                      )}
                    </>
                  );
                })()}
                money={rowPriceText(r, crossing) !== '' ? rowPriceText(r, crossing) : undefined}
                /* ⭐ board 2812 M3 (R-2812-h, SC-4): ONE chip.
                   ⭐⭐ board 2812 M3 fix round (Captain's layout items 1 and 3): a Need you
                   now row's chip says the ACTION, never just the state ("Price this" for a
                   new request (board 3020), "Confirm to book" for a priced one, "Answer the
                   change", "Answer the cancel": `requestActionChip`, read off
                   the same `needsHelenNow` place as the row's controls), so no row that
                   needs Helen wears a green "Confirmed". A Done row is quiet: its status
                   word in the same NEUTRAL box. Neither is toned (an action is not a
                   status, and the Done list is muted). */
                chip={where.place === 'needs'
                  ? <Chip neutral>{requestActionChip(where.primary, r)}</Chip>
                  : <Chip neutral>{word(STATUS_WORDS, r.status)}</Chip>}
              >
                  <div class="portal-request-body">
                    <div class="portal-request-actions">
                    {/* ⭐⭐ Board 2788 M6 (step 2; R-3, R-4, DD-5, DD-8, DD-11) - THE HOME'S
                        ASK, FIRST ON THE OPEN ROW, because it is the decision the row is
                        waiting for: the ask in words, labelled by `time_meaning`, and the
                        reason Decline needs (DD-5's three, Decline off until one is picked).
                        ⭐⭐ board 2812 M3 fix round 2 (PA-1, Captain's item 2; the round-2
                        mockup's `nursing-todo-cancel-ask` state): the ask's CONTROLS live in
                        the card's ONE action row at the bottom, with no separator: a CHANGE
                        ask's Accept is the gold, and Decline sits in the end slot beside the
                        request's own Cancel; a CANCEL ask has no gold, its "Cancel the trip"
                        (destructive, confirms in place) and Decline share the end slot. Asks
                        exist on Confirmed requests only (`portal/src/core/requests-repo.ts`,
                        `status = 'confirmed'`), whose own actions are Cancel alone, so the end
                        slot holds two. Every route is 2814's, unchanged.
                        ⛔ NOT SUBJECT TO THE 24-HOUR CUT-OFF (DD-19): Helen may answer at any
                        time; nothing here reads the clock. */}
                    {askLine !== null && (
                      <div class="portal-request-ask">
                        <p class="portal-request-mark">{askLine}</p>
                        {!askCancelArmed && !askDeclineArmed && (
                          <>
                            <div class="portal-request-field">
                              <FormField
                                id={`portal-decline-reason-${rowKey}`}
                                label="Why not?"
                                kind="select"
                                value={declineReason[r.requestId] ?? ''}
                                options={[
                                  { value: '', label: 'Pick a reason' },
                                  ...DECLINE_REASON_WORDS.map((d) => ({ value: d.value, label: d.label })),
                                ]}
                                onInput={(v: string) => setDeclineReason((prev) => ({ ...prev, [r.requestId]: v }))}
                              />
                            </div>
                            {/* ⭐ Board 2788 M7 (step 0, the M6 ux review): a Decline that is
                                off says WHY, as its description, so a greyed control is never a
                                puzzle. Gone once a reason is picked. */}
                            {(declineReason[r.requestId] ?? '') === '' && (
                              <p class="field-hint" id={`portal-decline-hint-${rowKey}`}>
                                Pick a reason to decline
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {/* ⭐ Board 2788 M6 (step 2, DD-2, DD-14) - THE HOME CHANGED IT. Price
                        does not follow the time on its own (DD-2), so a Changed Priced
                        row says to check the figure before the Confirm below it. */}
                    {changedPriced && (
                      <p class="portal-request-mark">
                        {r.status === 'priced'
                          ? 'Changed by the home: check the price before you confirm.'
                          : 'Changed by the home since it was sent.'}
                      </p>
                    )}
                    {/*
                      ⛔⛔ T1746 M5 (B7) - THE HOME PICK THAT USED TO OPEN THIS
                      PANEL IS GONE, AND WITH IT THE DESTINATION PICKER UNDER IT.

                      T1683 M1 moved the pick here from inside the crossing
                      reveal, because at pricing time the kiosk did not yet know
                      which account it was pricing for. M5 answers that question
                      without asking it: the offer route derives the home from
                      the request's own key, so the panel opens straight onto the
                      answer. ⛔ DO NOT PUT A `<select>` BACK IN HERE - see this
                      file's header for why a pick can only ever disagree with
                      the crossing now.
                    */}
                    {panelOpen && (
                      <div id={panelId}>
                        {/* ⚠️ T1746 M5 (B7) - THE PANEL'S EXPLANATION BLOCKS
                            STOOD HERE AND HAVE COLLAPSED INTO ONE LINE, in the
                            live region below: the home `<select>` and its
                            "not linked to a billing account yet" hint; the
                            curated row's "Priced for X, the destination the home
                            chose"; the destination `<select>`, its "Nothing is
                            chosen for you" hint and its failure Banner; and the
                            scope paragraph ("An agreed price covers the whole
                            vehicle, one way, for the home you picked. Nothing is
                            added for waiting time, an escort or an accessible
                            vehicle.", plus a conditional return sentence).
                            ⛔ EVERY ONE OF THEM EXISTED TO EXPLAIN A QUESTION
                            THIS PANEL NO LONGER ASKS. The offer route answers
                            per REQUEST, and its six kinds each get one sentence
                            that names the home and the destination outright, so
                            the operator reads the answer rather than an
                            explanation of how to reach it.
                            ⚠️ WHAT WAS LOST WITH THE SCOPE PARAGRAPH, recorded
                            rather than smuggled: the words "waiting time",
                            "escort" and "accessible vehicle" are no longer on
                            screen. An account price is still all-in and still
                            covers none of those; it is now stated in the offer
                            route's docblock only. ⇒ Bring the sentence back if
                            an operator asks what the figure includes - beside
                            the answer, not in front of it. */}
                        {priceHelpId === r.requestId && (
                          <button
                            type="button"
                            class="btn btn--quiet btn--inline"
                            // ⭐ THE OTHER HALF OF ONE TOGGLE (T1746 M5, ux WARN
                            // 3). `Use the agreed price` and `Done` are two
                            // elements but one control: the first only renders
                            // while the panel is shut, the second only while it
                            // is open. Both carry the SAME `aria-controls`, and
                            // `aria-expanded` is read off `panelOpen` rather
                            // than hard-coded, so the attribute cannot drift
                            // from the thing it describes.
                            aria-expanded={panelOpen}
                            aria-controls={panelId}
                            disabled={stepOpen}
                            onClick={() => setPriceHelpId(null)}
                          >
                            Done
                          </button>
                        )}
                      </div>
                    )}

                    {PRICEABLE.has(r.status) && !panelOpen && (
                      <button
                        type="button"
                        class="btn btn--quiet btn--inline"
                        // ⭐ T1746 M5 (ux WARN 3). See the `Done` button above:
                        // one toggle, two elements, one `aria-controls`.
                        aria-expanded={panelOpen}
                        aria-controls={panelId}
                        disabled={stepOpen}
                        onClick={() => {
                          // ⛔ ONE PANEL AT A TIME, on the whole screen.
                          setCrossingId(null);
                          setPriceHelpId(r.requestId);
                          // ⛔⛔ THIS PRESS IS THE ASK (T1746 M5, B7). The panel
                          // used to open onto a home question and only reach the
                          // price after she answered it; there is nothing to
                          // answer now, so opening it IS the request for a
                          // figure and the read starts here.
                          // ⛔ HERE AND NOT ON `panelOpen`, deliberately. The
                          // CROSSING opens the same panel, on a row that has
                          // already been priced and saved - firing the offer
                          // there would repaint the euro box underneath a
                          // booking confirmation, which is a money field moving
                          // for a reason the operator did not ask for.
                          resolveAgreedPrice(r.requestId);
                        }}
                      >
                        Use the agreed price
                      </button>
                    )}

                    {/* ⭐⭐ THE ANSWER LIVES OUT HERE, NOT IN THE PANEL, AND
                        THAT PLACEMENT IS THE GUARD (T1683 M1 review, security
                        F1). `offeredEuros` fills the euro box on
                        `lookup.kind === 'priced'` ALONE, so a sentence gated on
                        the panel as well left the figure stranded the moment
                        the panel closed - by `Done`, or by the
                        one-panel-at-a-time rule when another row's panel opens.
                        On a RETURN job that stranded figure is DOUBLE the
                        agreed price with nothing on screen saying so, and Save
                        sends it. ⚠️ This read "With Return trip latched" until
                        the M7 review: "latched" named M1's deleted operator
                        tick. The hazard is the same; its driver is now
                        `r.returnTrip`, the nurse's answer off the seam. Out here, one condition really does drive
                        both, and the sentence sits directly above the box it
                        explains.

                        ⚠️ MOUNTED UNCONDITIONALLY so the answer is ANNOUNCED: a
                        live region has to exist in the DOM before its content
                        changes. `aria-busy` holds the announcement until the
                        answer lands instead of reading out "Checking…" and
                        interrupting itself. Both copied from Quote.tsx, where
                        the same lookup already renders.

                        ⛔ ONE REGION, NOT TWO. Splitting "Checking…" from the
                        answer would defeat the `aria-busy` hold it was added
                        for: the two states would announce separately and the
                        first would interrupt the second. */}
                    {/* ⭐ `id` FOR THE EURO BOX'S `aria-describedby` (T1746 M5,
                        ux WARN 2), AND IT IS ON THE REGION RATHER THAN ON EACH
                        LINE. Exactly one of the seven children below renders at
                        a time (they are branches on one `kind`), so one id on
                        the wrapper resolves to whichever sentence is current -
                        including `Checking the agreed price…` and the `failed`
                        Banner. Seven ids would be seven chances for a new kind
                        to arrive without one, and a sighted operator would see
                        the line while a screen-reader user heard nothing. */}
                    <div id={offerId} aria-live="polite" aria-busy={price.kind === 'checking'}>
                      {price.kind === 'checking' && (
                        <p class="field-hint">Checking the agreed price…</p>
                      )}
                      {/* ⭐ THE FIGURE OUTRANKS THE PARAGRAPH THAT EXPLAINS IT
                          (T1683 M1 review, ux F3). This confirmed figure wore
                          `.queue-evidence` alone - grey caption, no weight - on a
                          screen whose whole point is a number Helen reads out
                          loud and bills a nursing home for. `.queue-evidence`
                          still sets the rhythm; `.price-basis` is the emphasis,
                          in existing tokens rather than a new colour. */}
                      {/* ⛔⛔ ONE LINE PER KIND, AND EACH ONE NAMES THE HOME OR
                          THE DESTINATION OUTRIGHT (T1746 M5, B7). The panel asks
                          nothing now, so the answer has to say what it is an
                          answer ABOUT - otherwise "No agreed price" floats over
                          a row whose home the operator never stated.

                          ⛔ BOTH FIGURES STAY ON THE PRICED LINE: the ONE WAY
                          basis and what it became. A single doubled number with
                          no statement of what it is a price FOR is how a one-way
                          figure gets sent as a return.
                          ⛔⛔ AND THE SECOND FIGURE IS `totalCents`, THE SERVER'S
                          OWN ARITHMETIC (S9). It read
                          `formatEuro(oneWayCents * 2)` until M5. Doing the
                          multiplication here is not a harmless duplicate: the
                          box now prefills from `totalCents`, so a client-side
                          `× 2` in this sentence could disagree with the very
                          figure it claims to explain. The arm is chosen by
                          `price.returnTrip` - the nurse's answer, re-read off
                          the request by the handler that did the doubling - so
                          the words and the number come from ONE answer. */}
                      {/* ⭐⭐ T2088 M7 (plan D5) — THE DOUBLED FIGURE NEVER
                          APPEARS WITHOUT ITS CAVEAT. D5 ruled a return whose
                          time is unknown is priced as a FULL return, and this
                          sentence is where Helen meets that arithmetic; the
                          marker says the second leg has no time YET, so the
                          figure is right and the journey is incomplete. Without
                          it the row read exactly like a return with a known
                          time, which is the ambiguity this milestone exists to
                          remove.
                          ⛔ THE MARKER IS `r.returnAtState`, THE ROW'S OWN SEAM
                          VALUE, NOT A SIXTH KEY ON `AgreedPrice`. That type is a
                          SERVER response about a price book (`/api/portal/
                          requests/:id/agreed-price`); widening it for a fact the
                          row already carries would put one answer on two wires
                          and invite them to disagree. `price.returnTrip` still
                          picks the ARM, for the reason the block above gives —
                          the words and the number must come from one answer.
                          ⛔ ONE SPACE, NO COMMA, NO DASH: `DESIGN.md` §7.6's card
                          grammar runs the phrase and its time together, and the
                          nurse's own two lists already read this way. */}
                      {price.kind === 'priced' && (
                        <p class="queue-evidence price-basis">
                          {price.returnTrip
                            ? `Agreed price for ${price.destinationName}: ${formatEuro(price.oneWayCents)} one way, doubled for the return: ${formatEuro(price.totalCents)}.${r.returnAtState === 'to-confirm' ? ` Return ${RETURN_TIME_TO_CONFIRM}.` : ''}`
                            : `Agreed price for ${price.destinationName}: ${formatEuro(price.oneWayCents)}.`}
                        </p>
                      )}
                      {/* ⛔ ONE SENTENCE FOR `unpriced` AND `missing`, AND THAT
                          IS A NARROWING OF AN EARLIER RULE RATHER THAN A BREACH
                          OF IT. D-RESOLVE keeps the two kinds apart all the way
                          to this screen, and must: collapsing them in the
                          RESOLVER is how "not on their card yet" becomes "no
                          price". What the OPERATOR does about them is identical
                          - type the figure - and the difference (is the
                          destination on the home's list at all?) is a price-book
                          fact she cannot act on from this row. ⇒ Split the copy
                          the moment this screen grows a way to put a figure ON
                          the home's card, which is the only act the distinction
                          would change. */}
                      {(price.kind === 'unpriced' || price.kind === 'missing') && (
                        <p class="field-hint">
                          {`No agreed price for ${price.destinationName} on ${price.homeName}'s list. Type the figure.`}
                        </p>
                      )}
                      {/* ⛔ THE SAME SENTENCE THE SERVER REFUSES THE CROSSING
                          WITH (`crossing_home_unlinked`), said BEFORE she gets
                          there rather than after. An unlinked home has no price
                          book to read, so there is no figure to offer and the
                          repair is on another screen. */}
                      {price.kind === 'unlinked' && (
                        <p class="field-hint">
                          {/* ⭐ board 2812 M4 fix round (L7): the Homes tab is gone; linking is in
                              Set up homes and places, at the column's foot. */}
                          {`${price.homeName} is not linked to a billing account yet. Link it in Set up homes and places first.`}
                        </p>
                      )}
                      {/* ⛔ THE LEGACY / FREE-TYPED ROW, AND IT IS NOT AN ERROR.
                          The request carries no destination of ours to price
                          against - a row written before the home's catalogue was
                          projected, or one the nurse typed herself - so there is
                          nothing to look up and never was. It names the home so
                          the line cannot be read as being about a different row.
                          ⚠️ THIS IS WHERE THE RETIRED DESTINATION PICKER USED TO
                          LEAD; see the file header for the reason it did not
                          survive and the trigger that would bring it back. */}
                      {price.kind === 'no-destination' && (
                        <p class="field-hint">
                          {`This request did not come with one of our destinations, so ${price.homeName} has no agreed price to offer for it. Type the figure.`}
                        </p>
                      )}
                      {/* ⛔ THE SCREEN IS OUT OF DATE, AND IT SAYS SO WITHOUT
                          BLAMING HER OR NAMING A HOME IT CANNOT IDENTIFY. The
                          portal stated no usable home for this request, or one
                          this portal no longer lists; both have the same repair,
                          and it is a RELOAD rather than an answer she can give
                          here, so there is no control beside it. It is the same
                          sentence the crossing refuses with
                          (`crossing_home_unknown`), which is deliberate: two
                          surfaces saying one thing two ways is how an operator
                          ends up told two stories about one request. */}
                      {price.kind === 'unknown-home' && (
                        <p class="field-hint">
                          The portal did not say which home this request belongs to. Reload
                          the list and try again.
                        </p>
                      )}
                      {/* ⛔ A FAILED CHECK LOOKS DIFFERENT FROM "NO PRICE ON
                          FILE", and that is a billing control: one is
                          actionable, the other is not, and they used to
                          render identically on the Quote screen. */}
                      {price.kind === 'failed' && (
                        <Banner kind="error">
                          Could not check the agreed price. Nothing is filled in. Try again
                          in a moment.
                        </Banner>
                      )}
                    </div>

                    {PRICEABLE.has(r.status) && (
                      /* ⭐⭐ T1749 M7 wave-3 (impeccable P2-3) — A WRAP UNIT, NOT
                         TWO FLEX ITEMS. Measured at 1024: as independent children
                         of the wrapping action row, `Note for the home`'s label
                         landed at x 798 on row one and its input at x 257 on row
                         two, so a field's name sat above a different field's box.
                         Wrapping each label-and-input pair makes the PAIR the
                         thing that wraps. */
                      <div class="portal-request-field">
                        {/* ⛔ T1683 M6 — `required` IS THE DECLARATION, NOT THE
                            GUARD. `act` already refuses an empty or malformed
                            figure with a sentence that says why, and this input
                            sits in no <form> so nothing here blocks a press.
                            ⭐⭐ T1749 M7 rework 1 — `FormField`, and the two props it
                            had to grow for this field to survive adoption:
                            `opaque` (the recorder's mark, on the control) and
                            `describedBy`, which keeps the live agreed-price line
                            named beside the error. Without the second, T1746 M5's
                            fix would have been deleted by the adoption: a screen
                            reader heard "Price, edit text, required" over a box
                            that had silently prefilled a figure which on a return
                            job is double the agreed one.
                            ⚠️ ERROR FIRST, THEN THE OFFER: `describedByFor` puts
                            the error ahead of the extras, which is the order this
                            field already announced by hand. */}
                        <FormField
                          id={`portal-amount-${rowKey}`}
                          label="Price"
                          value={amountText(r.requestId)}
                          inputMode="decimal"
                          required
                          opaque
                          /* ⭐ board 2814 M8R1 (R9): NO placeholder. A fixed "125.00"
                             showed on a row whose own price was €60, a figure that
                             looks like a price and is nobody's. (Board 2812 M3's UX-6
                             showed the card's own price here; the sibling's ruling won
                             at board 2812 M5's Keep current.) */
                          /* ⛔⛔ THE SENTENCE ITSELF, NOT A FLAG (T1749 M7 rework 1).
                             `FormField` renders the error under the field, which
                             is the house shape and is where T1719 M7's finding put
                             it: the banner at the top of a list a dozen rows long
                             can be off the bottom of the viewport from the box it
                             is about. The banner KEEPS the sentence and its
                             `role="alert"`; this is redundancy at the field, not a
                             move, and the two read from ONE state. */
                          error={hasAmountError ? amountError?.text : undefined}
                          describedBy={[offerId]}
                          disabled={stepOpen}
                          onInput={(v: string) =>
                            setAmounts((prev) => ({ ...prev, [r.requestId]: v }))
                          }
                        />
                        {/* ⭐ board 2812 M3 fix round (Captain's layout item 2): `Save price` and
                            `Update price` LEFT the field for the card's ONE action row at the
                            bottom (the gold Confirm or Save price first). T1749 M7's measured
                            reason for siting it here (R11's ~700 plane) is held by the shorter
                            head instead: the when cell is two lines (B8), and tablet-ia SC-10c
                            and SC-10d still grade the gold against the plane. */}
                      </div>
                    )}

                    {/* ⭐⭐ T1749 M7 (R4) — ONE GOLD PRIMARY ON THE OPEN ROW, AND IT
                        IS THE ROW'S PENDING DECISION. `new` is waiting for a price;
                        `priced` is waiting for a confirm. Everything else on the row
                        is quiet, and the two ENDING acts keep the destructive
                        grammar.
                        ⛔ EXACTLY ONE ON THE SCREEN, WHICH IS §5.2's RULE AND NOT A
                        COINCIDENCE: one row is open at a time (`openId`) and only
                        the open row renders any of these controls at all.
                        ⚠️ THE LABELS DO NOT MOVE. They are net-locked names and
                        rewording one is a copy decision; what changed is which of
                        them wears the gold. */}
                    {/* ⭐⭐ Board 2814 M1 (D2) — WHY THERE IS NO CONFIRM, SAID WHERE IT
                        WOULD BE, and never in gold: a past day with no live booking
                        was not booked here and will not be now. ⛔ An open ask has
                        no line here: 2788 M6's ask block at the top of this body is
                        that row's decision (Accept, or Cancel the trip, and
                        Decline), and M1's interim line saying so went at the
                        rebase onto it. */}
                    {/* ⭐⭐ Board 2814 M2 (DD-11) - THE ROW'S OWN WORDS from the table, said
                        where a Confirm would be and never in gold: the date passed (rows
                        2 and 4), a cancelled booking (6), End the run (12), settled by
                        hand (14, board 2814 M4), or the link could not be read. */}
                    {where.line !== null && <p class="queue-evidence">{where.line}</p>}
                    {/* ⭐⭐ T1447 M7 — THE CROSSING, RESTING. Its two-step
                        reveal moved UP into the shared panel above (T1683 M1),
                        because the pick it opens with is the SAME home pick the
                        agreed-price lookup needs — and that lookup has to happen
                        BEFORE the row is priced, while this button only appears
                        AFTER it. One picker, two steps. Confirm still clears the
                        reveal FIRST and fires SECOND, so a slow write cannot
                        leave the step open to be pressed again. */}
                    {/*
                      ⭐⭐ T1746 M3 (Captain's M2 gate item 2a) - WHEN THE KIOSK
                      COULD NOT READ ITS OWN CROSSINGS, THE ROW SAYS SO AND
                      OFFERS NOTHING. `crossings === null` is not "nothing has
                      crossed": under M1's empty-map degrade this row restored
                      "Make it a booking" on a request that may already BE a
                      booking. ⭐ Board 2814 M2: the neutral line is the table's
                      `line` for a link-unknown row (DD-11), said once above the
                      actions, and that row offers no Confirm and no gold.
                    */}

                    {/* ⚠️ Board 2814 M1 (DD-2) - "Make it a booking" STOOD HERE AND IS
                        GONE, with its panel: Confirm is the one press that books a
                        request now, and two controls for one decision is the shape
                        DESIGN.md §5.1's confirm-in-place grammar exists to remove. */}

                    {/*
                      ⭐⭐ T1746 M1 (B4, Q108 option b) - WHAT THIS REQUEST
                      ALREADY MADE, IN PLACE OF THE CONTROL THAT WOULD MAKE IT
                      AGAIN.

                      ⛔ IT REPLACES `Make it a booking` RATHER THAN JOINING IT.
                      The crossing is idempotent, so a second press is safe in
                      the database and dishonest on the screen: it says a
                      decision is still Helen's when it was taken. The row is
                      simply no longer crossable.

                      ⛔ A CANCELLED-THEN-RECONFIRMED REQUEST KEEPS SHOWING THIS
                      BOOKING, and that is the UNIQUE talking, not a bug:
                      `inquiries_portal_request_uk` is keyed on the request id
                      alone, so a request that crossed once can never cross
                      again (open question Q107, case 1, `docs/OPEN-QUESTIONS.md`).
                      Nothing here decides that question either way.
                    */}
                    {crossing !== undefined && (
                      /* ⛔ A FRAGMENT, NOT A WRAPPER DIV. The cell is already a
                         stack and this screen's stylesheet has no rule for a
                         new container, so an unstyled wrapper would be one more
                         box with no declaration behind it (the P1-2 contract
                         test/client/style-contract.spec.ts holds). */
                      <>
                        {crossing.bookingId === null ? (
                          /*
                            ⛔ LANDED, NOT BOOKED, AND SAID PLAINLY. The crossing
                            is two transactions: the request lands as an
                            `inquiries` row and the BOOKING is made after it, so
                            a reference collision (`server/app.ts`) leaves
                            exactly this state.
                            ⭐⭐ Board 2814 M1 (DD-2) - THE EXIT IS THE ROW'S OWN
                            CONFIRM NOW, not the calculator. It read "Key the
                            booking by hand in the calculator, choosing the home's
                            account" (T1746 M2, B9); the same press that books a
                            Priced request completes a landing, and a refusal (a
                            reference clash, a missing agreed price) comes back
                            in words naming the cause. Where the row offers no
                            Confirm (a past day, an open ask) it says only what
                            happened, never "press Confirm".
                          */
                          <p class="queue-evidence">
                            {confirmKind === 'step'
                              ? 'Landed here, but no booking was made yet. Press Confirm to make it.'
                              : 'Landed here, but no booking was made yet.'}
                          </p>
                        ) : (
                          <>
                            {/* ⭐⭐ Board 2788 M6 (DD-17) - THE MISMATCH MARK, only under
                                DD-17's condition (`bookingRequestMismatch`), beside the
                                control that opens the booking where it is put right. */}
                            {/* \u2b50\u2b50 Board 2788 M7 (step 0, the M6 wave's security
                                Medium): the steer follows the side that moved last
                                (`mismatchSteer`), never `homeChanged`, which Helen's
                                DD-2 re-price clears. */}
                            {mismatch !== null && (
                              <p class="portal-request-mark">
                                {`The booking says ${mismatch.booking} but the home's request says ${mismatch.request}. `}
                                {mismatchSteer(r, crossing) === 'change'
                                  ? 'Open the booking and change it to the home\u2019s time, then confirm.'
                                  : 'Open the booking and send it to the home again.'}
                              </p>
                            )}
                            {/* ⭐ board 2812 M3 fix round: DD-11 row 10's gold "Open the booking"
                                moved to the card's ONE action row at the bottom. */}
                            {/*
                              ⛔ THE ACCESSIBLE NAME IS THE ACTION, THE TEXT IS
                              THE FACT. A screen reader hears "Open booking
                              ECM-...", which is what the press does; the visible
                              line carries the state as well, in the registry's
                              own word so it reads the same here as on the
                              booking itself.
                            */}
                            <button
                              type="button"
                              class="btn btn--quiet"
                              // ⛔ board 2812 M3 fix round 2 (SEC-1): its name carries the JOB
                              // REFERENCE, and the click recorder reads `aria-label`, so it is
                              // opaque: the trail records THAT it was pressed, never which booking.
                              data-fb-opaque
                              aria-label={`Open booking ${bookingRef(crossing)}`}
                              onClick={() => onOpenBooking(crossing.bookingId as number)}
                            >
                              {/* ⭐ board 2814 M7 (DD-7): the taxi mark, kiosk-local (the crossing summary). */}
                              {/* ⛔ The reference never breaks mid-word (board 2812 M3 fix round,
                                  Captain's item 4): its own `.job-ref-value` span. */}
                              Booking <span class="job-ref-value">{bookingRef(crossing)}</span>
                              {` · ${bookingStatusLabel(registry, crossing.status ?? '')}${crossing.taxiPassed === true ? ', passed to a taxi' : ''}`}
                            </button>
                            {/*
                              ⭐⭐ T2088 M8 (R-4, leg A) — THIS PARAGRAPH BECAME A
                              CONTROL. It used to read "Open booking X and cancel
                              the run there as well", which is an instruction to
                              leave the screen and do by hand what a button can
                              do: Q108 option (b), named but not built. It is
                              built here.

                              ⛔ IT IS GATED ON THE BOOKING'S STATUS, NOT THE
                              REQUEST'S, through the SHARED predicate
                              `isBookingCancellable` that `BookingDetail`'s own
                              cancel controls read. Two hand-written copies of a
                              money-bearing visibility rule is how one screen
                              keeps offering a control the other has retired.

                              ⛔ IT CONFIRMS IN PLACE, exactly as the two ending
                              acts above it do, and that is DESIGN.md §5.1 rather
                              than a preference: this screen's rulebook reference
                              for a destructive act is confirm-in-place, and this
                              act is strictly MORE destructive than the two that
                              already obey it — it cancels a real run, a booking
                              cannot be un-cancelled anywhere in this product
                              (`data/bookings-repo.ts` writes every status change
                              `WHERE status NOT IN ('cancelled','completed')`),
                              and no screen offers an undo. ⚠️ Alex's board ruling
                              says "one-click cancel": that is read here as ONE
                              CONTROL ON THE ROW rather than a trip to another
                              screen, which is the thing Q108(b) was about. If he
                              meant literally no confirm, this limb is the one
                              thing to delete — it is recorded in the Session Log
                              so the question is asked rather than assumed.

                              ⚠️ IT REUSES `endingAct` under a sentinel status, so
                              exactly ONE confirm is up at a time across the whole
                              row — arming this one disarms a half-tapped Decline,
                              which is the property that state was introduced for.
                            */}
                            {/* ⭐ Board 2788 M6: not while the home's cancel ask is open;
                                accepting it ends the run (see the ask block above).
                                ⭐ Board 2814 M2 (N1): `canEndRunHere`, the predicate
                                DD-11 row 12 reads, so a TRAVELLED run offers no End
                                the run in any list. ⭐ Board 2814 M4 (Captain ruling
                                (c)): a travelled run stays on the month's bill from
                                every Requests-screen control, and the server refuses
                                this post without booking detail's acknowledgement
                                (`run_travelled`), so a stale row is refused too.
                                Booking detail's acknowledged cancel is the only
                                correction path. */}
                            {canEndRunHere(crossing) && !cancelAsked && (
                              endingAct !== null
                              && endingAct.requestId === r.requestId
                              && endingAct.status === BOOKING_CANCEL_ACT
                                ? (
                                  <ConfirmInPlace>
                                    <p class="queue-evidence">
                                      {`End the run for booking ${bookingRef(crossing)}? It comes off the sheet and off the month's bill. It cannot be undone here.`}
                                    </p>
                                    {/* ⭐ board 2812 fix round (item 9): the keep FIRST, so it
                                        takes the focus (`ConfirmInPlace`), and the red Yes in
                                        the end slot at the far end (R-2812-d, §5.1). */}
                                    <ActionRow
                                      end={(
                                        <Button
                                          variant="destructive-quiet"
                                          disabled={busyId === r.requestId}
                                          onClick={() => {
                                            setEndingAct(null);
                                            void cancelRun(r, crossing.bookingId as number, bookingRef(crossing));
                                          }}
                                        >
                                          Yes, end the run
                                        </Button>
                                      )}
                                    >
                                      <Button variant="secondary" onClick={() => setEndingAct(null)}>
                                        Keep the run
                                      </Button>
                                    </ActionRow>
                                  </ConfirmInPlace>
                                )
                                : (
                                  <Button
                                    variant="destructive-quiet"
                                    disabled={busyId === r.requestId}
                                    onClick={() => setEndingAct({ requestId: r.requestId, status: BOOKING_CANCEL_ACT })}
                                  >
                                    End the run
                                  </Button>
                                )
                            )}
                          </>
                        )}
                      </>
                    )}
                    {/* ⭐⭐ board 2812 M3 fix round (Captain's layout item 2) — THE NOTE FOR THE
                        HOME SITS ABOVE THE CARD'S ONE ACTION ROW: the facts, then the note,
                        then the actions. T1749 M7 put it below the actions because it pushed
                        the gold past R11's ~700 plane on a head whose when cell ran to five
                        lines; that head is two lines now (B8), and tablet-ia SC-10c, SC-10d
                        and SC-10g still grade the gold against the plane.
                        ⭐ (item 3) A DONE ROW HAS NO NOTE BOX: nothing it could send goes
                        with it, so the note is shown read-only when it has text. */}
                    {where.place === 'done'
                      ? ((notes[r.requestId] ?? r.waitingNotes ?? '') !== '' && (
                        <p class="queue-evidence" data-fb-redact="">{`Note for the home: ${notes[r.requestId] ?? r.waitingNotes ?? ''}`}</p>
                      ))
                      : (
                      <div class="portal-request-field">
                        {/* ⭐ T1749 M7 rework 1 — `FormField`, with `opaque`: this box
                            holds a note about a named resident, so the recorder must
                            see the control as opaque or its text reaches the click
                            stream. */}
                        <FormField
                          id={`portal-note-${rowKey}`}
                          label="Note for the home"
                          value={notes[r.requestId] ?? r.waitingNotes}
                          maxLength={500}
                          opaque
                          /* ⭐ Board 2814 M8R1 (R4): off while the confirm step is open. */
                          disabled={stepOpen}
                          onInput={(v: string) =>
                            setNotes((prev) => ({ ...prev, [r.requestId]: v }))
                          }
                        />
                      </div>
                      )}
                    {/* ⭐ board 2812 M3 fix round: the action row stays INSIDE the wrapping
                        region, after the price and the note, so a destructive above it (End
                        the run, the ask's own Decline) never sits block-adjacent to its gold
                        (§5.1's limb (b)); `.portal-request-actions > .ui-actions` gives it a
                        whole line, so its end slot reaches the card's far end. A confirm that
                        takes its place lands in the same slot. */}
                    {/* ⭐⭐ board 2812 M3 fix round (Captain's layout item 2; R-2812-d as the
                        driver reads it, recorded for Alex) — ONE ACTION ROW AT THE BOTTOM OF
                        THE CARD: the gold (Confirm, Save price, or row 10's Open the booking)
                        first in the start group, then the quiet ones (Update price, the two
                        settle controls, Undo); the request's ending acts, Decline and Cancel,
                        together in the END SLOT. No separator row and no second action row.
                        ⛔ A CONFIRM REPLACES THE ROW, it never sits beside it: Confirm's step
                        (DD-2) and the one armed ending act (`endingAct`, one at a time on a
                        card) each take the row's place, so the gold is hidden while a
                        destructive question is up (§5.1, BookingDetail's grammar). What each
                        control does is 2814's, unchanged. */}
                    {(() => {
                      /** One request action's control (2814's markup, unchanged). */
                      const actionButton = (action: { status: string; label: string }) => {
                        // ⭐ Board 2814 M1: the row's Confirm, when it has one, is
                        // always its gold primary (DD-2), on a Confirmed row too.
                        const primary = action.status === 'confirmed' || action.status === PRIMARY_ACTION[r.status];
                        return (
                            <button
                              key={action.status}
                              type="button"
                              class={
                                primary
                                  ? 'btn btn--primary btn--inline'
                                  : `btn btn--quiet btn--inline${ENDING.has(action.status) ? ' btn--destructive' : ''}`
                              }
                              disabled={busyId === r.requestId}
                              onClick={() => {
                                if (ENDING.has(action.status)) {
                                  setEndingAct({ requestId: r.requestId, status: action.status });
                                } else if (action.status === 'confirmed' && confirmKind === 'step') {
                                  // ⭐ Board 2814 M1 (DD-2): the press opens the step;
                                  // nothing is written until "Yes". One step and one
                                  // panel at a time on the whole screen.
                                  setPriceHelpId(null);
                                  setEndingAct(null);
                                  setDuplicate(null);
                                  setCrossingId(r.requestId);
                                } else {
                                  void act(r, action.status);
                                }
                              }}
                            >
                              {/* ⭐ T1683 M6 — THE CONFIRM AFFORDANCE. On a curated row
                                  holding an untouched resolved figure, the money button
                                  names what the press actually is: Helen did not write
                                  this number, she is agreeing to it. Same write, same
                                  body — see `isConfirmation`. */}
                              {action.status === 'priced' && isConfirmation(r.requestId)
                                ? 'Confirm the agreed price'
                                : action.status === 'cancelled' && changeAsked
                                  /* ⭐ board 2812 fix round (item 6): beside "Decline the change",
                                     the request's Cancel says it ends the trip. */
                                  ? CHANGE_CARD_CANCEL
                                  : action.label}
                            </button>
                        );
                      };
                      for (const action of actionList) {
                        if (action.status === 'confirmed' && confirmKind === 'step' && crossingId === r.requestId && !crossingsUnknown) {
                          const dup = duplicate !== null && duplicate.requestId === r.requestId ? duplicate.matches : null;
                          const note = noteText(r);
                          return (
                            <ConfirmInPlace key="confirm-step">
                              {/* ⛔⛔ Board 2814 M1 fix round (DD-2) — THE ACTION PAIR SITS
                                  ABOVE EVERY CAVEAT, and the order is the fix, not a
                                  tidy-up. Measured on a private seat at 1024x700 with
                                  the row's handle at the top of the pane: with the
                                  three-line snapshot sentence and the read-only note
                                  ABOVE the pair, the gold "Yes" ended at 720px, past
                                  the ~700 plane DD-2 holds it to (re-measure on a
                                  private seat: `npx playwright test tests/green/
                                  tablet-ia.spec.ts --grep SC-10g` from `e2e/`). So
                                  above the pair: who pays, which home, the band;
                                  below it, as the same quiet caption: the snapshot
                                  caveat, the repeating, no-date and no-time caveats,
                                  and the note line. The duplicate answer stays where
                                  it was, just above the pair it changes. */}
                              {/* ⭐ Board 2814 M4 (DD-2, DD-5): the payer in words, cash
                                  too now that cash crosses. A cash run sits on the home's
                                  account as the home link and is never billed to it. */}
                              {r.payerKind === 'self'
                                ? (
                                  <>
                                    <p class="queue-evidence">Cash, paid to the driver on the day.</p>
                                    <p class="queue-evidence">
                                      {`It is booked for ${r.homeName}, the home this request came from. The home is not billed for it.`}
                                    </p>
                                  </>
                                )
                                : (
                                  /* ⭐ Board 2814 M8R1 (R4): the payer in the row's own words
                                     (`PAYER_WORDS`, never a second copy) with the home it bills,
                                     "HSE / Rehab, billed to {home}.", in ONE line where two said
                                     "Billed to the home's account." and the home. Cash keeps
                                     DD-5's words: it is never billed to the home. */
                                  <p class="queue-evidence">
                                    {`${word(PAYER_WORDS, r.payerKind)}, billed to ${r.homeName}.`}
                                  </p>
                                )}
                              {/* ⭐ Board 2814 M8R1 (R4): the date and the price this press
                                  books, as the row already says them (`whenText`,
                                  `rowPriceText`). */}
                              <p class="queue-evidence">
                                {`${whenText(r)}${rowPriceText(r, crossing) === '' ? '' : `, ${rowPriceText(r, crossing)}`}.`}
                              </p>
                              {/* ⛔ NO DEFAULT, AND IT APPEARS ONLY NOW. The nurse's form
                                  has no passenger count, so a default would be a silent
                                  mispricing; Helen answers it at the press. */}
                              <FormField
                                id={`portal-band-${rowKey}`}
                                label="How many are travelling?"
                                kind="select"
                                opaque
                                value={bandPick[r.requestId] ?? ''}
                                options={[
                                  { value: '', label: 'Choose a size…' },
                                  ...BANDS.map((b) => ({ value: b, label: b })),
                                ]}
                                onInput={(v: string) =>
                                  setBandPick((prev) => ({ ...prev, [r.requestId]: v }))
                                }
                              />
                              {dup !== null && <p class="queue-evidence" role="status">{maybeDuplicateText(dup)}</p>}
                              <div class="portal-request-confirm-actions">
                                {dup === null
                                  ? (
                                    /* ⭐ Board 2814 M8R1 (R4): off until a size is chosen. The
                                       handler still refuses a press with no band (`cross`), for
                                       a dispatched click; this says it before the press. */
                                    <Button
                                      variant="primary"
                                      disabled={busyId === r.requestId || (bandPick[r.requestId] ?? '') === ''}
                                      onClick={() => void cross(r, { bookAnyway: false })}
                                    >
                                      Yes, confirm and book it
                                    </Button>
                                  )
                                  : (
                                    <Button
                                      variant="secondary"
                                      disabled={busyId === r.requestId}
                                      onClick={() => void cross(r, { bookAnyway: true })}
                                    >
                                      Book it anyway
                                    </Button>
                                  )}
                                {/* ⭐ QUIET, the level every other "Not now" in the kiosk
                                    wears (`QueueView.tsx`, `RateList.tsx`): leaving the
                                    step is not a choice between actions, and the step
                                    keeps exactly one painted primary. */}
                                <Button
                                  variant="quiet"
                                  disabled={busyId === r.requestId}
                                  onClick={() => {
                                    setCrossingId(null);
                                    setDuplicate(null);
                                  }}
                                >
                                  Not now
                                </Button>
                              </div>
                              {/* ⛔ WHAT WILL HAPPEN, carried from the retired reveal and
                                  still said before the press, now under the pair: the
                                  snapshot sentence is the only place an operator is
                                  told that a later re-link will not re-bill this run,
                                  and the repeating, no-date and no-time caveats are
                                  what the press creates. */}
                              <p class="queue-evidence">
                                The home is fixed at that moment: changing which account the home
                                belongs to later will not move this run.
                                {r.schedulePattern
                                  ? ' ⚠️ This is a repeating request, and this makes ONE booking from it, not a repeating run. Set the rest up yourself.'
                                  : ''}
                                {r.journeyDate === null
                                  ? ' There is no date on it, so the booking will have no date until you set one.'
                                  : ''}
                                {crossesWithoutTime(r)
                                  ? ' There is no collection time on it, so the booking will have no time until you set one.'
                                  : ''}
                              </p>
                              {/* ⭐ DD-2: the row's own "Note for the home", READ-ONLY
                                  here; the field stays on the row for Save price,
                                  Update price, Decline and Cancel. A confirmed request
                                  gets no mark, so it says no note is sent. */}
                              {r.status === 'priced'
                                ? (note !== '' && (
                                  <p class="queue-evidence" data-fb-redact="">{`Note for the home: ${note}`}</p>
                                ))
                                : (
                                  <p class="queue-evidence">
                                    The home&apos;s request is already confirmed, so no note is sent.
                                  </p>
                                )}
                              {/* ⭐ Board 2814 M8R1 (R4): the note the NURSE wrote on the
                                  request, exactly as the row's facts show it ("Nurse’s note",
                                  the same `nurseNotes` read, no new data path), and with the
                                  same `data-fb-redact`. Below the pair, so 500 characters
                                  cannot push "Yes" past the ~700 plane (SC-10g). */}
                              {nurseNote !== undefined && (
                                <p class="queue-evidence" data-fb-redact="">{`Nurse’s note: ${nurseNote}`}</p>
                              )}
                            </ConfirmInPlace>
                          );
                        }
                      }
                      for (const action of actionList) {
                        // ⛔ `!== null` FIRST, NEVER `?.` HERE, and the difference is a
                        // real crash rather than a style. `endingAct?.requestId` is
                        // `undefined` when nothing is armed; a row whose own
                        // `requestId` is also undefined (an older portal, a degraded
                        // projection) then makes the comparison TRUE and the next
                        // clause dereferences null, taking the whole list down.
                        // Measured on a real run. Same rule the malformed-row arm
                        // guards: never throw on a list row.
                        const confirming =
                          endingAct !== null
                          && endingAct.requestId === r.requestId
                          && endingAct.status === action.status;
                        if (ENDING.has(action.status) && confirming) {
                          const endingLine = endingScopeLine(action.status, linkedRun, crossingsUnknown);
                          return (
                            /* ⭐⭐ T1749 M7 (C6 + §5.1) — THE CONFIRM IN PLACE, AND THE
                               CANCEL FACT WITH IT. The limb REPLACES the button that
                               armed it, exactly as `admin/DestinationList.tsx` (the
                               rulebook's worked reference) does, so nothing moves on
                               the rest of the screen and the question is asked where
                               she just tapped. The sentence is the one the standing
                               paragraph carried, naming the booking when there is
                               one. */
                            <ConfirmInPlace key={action.status} endSlotAt={(askLine === null ? 0 : (r.askKind === 'change' ? 1 : 2)) + actionList.filter((a) => ENDING.has(a.status)).indexOf(action)}>
                              {/* ⭐ board 2812 M3 fix round: each confirm names its verb. A
                                  decline says what the home sees; the scope line follows,
                                  unchanged, because a decline on a booked row leaves the run
                                  live (`endingScopeLine`). The cancel's warning is today's. */}
                              {/* ⭐ board 2812 M5's Keep current: no question where 2814 M8R1
                                  offers no "Yes" (a travelled run keeps its request). */}
                              {/* ⭐ board 2812 fix round (Captain's /check item 6): the cancel names
                                  its verb and what the home sees too, and on a CHANGE card it says
                                  the whole trip goes (Decline there refuses only the change). */}
                              {!runTravelled && (
                                <p class="queue-evidence">
                                  {action.status === 'declined'
                                    ? DECLINE_CONFIRM_LINE
                                    : changeAsked ? CANCEL_TRIP_CONFIRM_LINE : CANCEL_CONFIRM_LINE}
                                </p>
                              )}
                              {/* ⭐ board 2812 M3 fix round 3 (F3-7), and for a CANCEL too since the
                                  fix round (item 6): what happens to the run is said only when there
                                  IS one (a linked booking), or when the links could not be read (a
                                  run may exist); on a request with no booking there is nothing to
                                  say about a run (`endingScopeLine` answers null). */}
                              {endingLine !== null && <p class="queue-evidence">{endingLine}</p>}
                              {/* ⭐ board 2812 fix round (item 9): Keep it FIRST, so it takes the
                                  focus (`ConfirmInPlace`) and a double Enter keeps the request;
                                  the red Yes in the end slot at the far end (R-2812-d, §5.1).
                                  ⭐⭐ Board 2814 M8R1 (Q287): a travelled run keeps its request
                                  open, so no "Yes" (`runTravelled`). */}
                              <ActionRow
                                end={runTravelled ? undefined : (
                                  <Button
                                    variant="destructive-quiet"
                                    disabled={busyId === r.requestId}
                                    onClick={() => {
                                      setEndingAct(null);
                                      void act(r, action.status);
                                    }}
                                  >
                                    {action.status === 'declined' ? 'Yes, decline it' : 'Yes, cancel it'}
                                  </Button>
                                )}
                              >
                                <Button variant="secondary" onClick={() => setEndingAct(null)}>
                                  Keep it
                                </Button>
                              </ActionRow>
                            </ConfirmInPlace>
                          );
                        }
                      }
                      const gold: VNode[] = [];
                      const quiet: VNode[] = [];
                      /* ⭐ board 2812 M3 fix round 2 (PA-1): the cancel ask's confirm takes the
                         row's place like every ending act; Keep it returns to "Cancel the trip",
                         the end slot's first control. */
                      if (askCancelArmed) {
                        const askCancelLine = endingScopeLine('cancelled', linkedRun, crossingsUnknown);
                        return (
                                <ConfirmInPlace endSlotAt={0}>
                                  {/* ⭐ board 2812 fix round (item 6): what the home sees, as every
                                      ending confirm on the card says; then the run's line, only
                                      when there is a run (or its links could not be read). */}
                                  {!runTravelled && (
                                    <p class="queue-evidence">
                                      {repeating ? ASK_STOP_CONFIRM_LINE : ASK_CANCEL_CONFIRM_LINE}
                                    </p>
                                  )}
                                  {askCancelLine !== null && <p class="queue-evidence">{askCancelLine}</p>}
                                  {/* ⭐ Board 2788 M7 step 0: Keep it FIRST and focused; ⭐ board 2812
                                      fix round (item 9): the red Yes in the end slot at the far end,
                                      as every confirm on the card.
                                      ⭐⭐ Board 2814 M8R1 (Q287): accepting a cancel ask closes the
                                      request, which a travelled run keeps open, so no "Yes". */}
                                  <ActionRow
                                    end={runTravelled ? undefined : (
                                      <Button
                                        variant="destructive-quiet"
                                        disabled={busyId === r.requestId}
                                        onClick={() => {
                                          setEndingAct(null);
                                          void answerAsk(r, 'accept');
                                        }}
                                      >
                                        {repeating ? 'Yes, stop the trip' : 'Yes, cancel the trip'}
                                      </Button>
                                    )}
                                  >
                                    <Button variant="secondary" onClick={() => setEndingAct(null)}>
                                      Keep it
                                    </Button>
                                  </ActionRow>
                                </ConfirmInPlace>
                        );
                      }
                      /* ⭐ board 2812 M3 fix round 3 (F3-2, SC-6): the ask's Decline confirms in
                         place with the request's own pair; Keep it returns to Decline, whose
                         end-slot position is 0 beside a change ask's Cancel and 1 after a
                         cancel ask's "Cancel the trip". */
                      if (askDeclineArmed) {
                        return (
                          <ConfirmInPlace endSlotAt={r.askKind === 'change' ? 0 : 1}>
                            {/* ⭐ board 2812 M4 (step 0 (a)): the question above the pair. */}
                            <p class="queue-evidence">{askDeclineQuestion(r)}</p>
                            {/* ⭐ board 2812 fix round (item 9): Keep it FIRST and focused, the
                                red Yes in the end slot at the far end. */}
                            <ActionRow
                              end={(
                                <Button
                                  variant="destructive-quiet"
                                  disabled={busyId === r.requestId}
                                  onClick={() => {
                                    setEndingAct(null);
                                    void answerAsk(r, 'decline');
                                  }}
                                >
                                  Yes, decline it
                                </Button>
                              )}
                            >
                              <Button variant="secondary" onClick={() => setEndingAct(null)}>
                                Keep it
                              </Button>
                            </ActionRow>
                          </ConfirmInPlace>
                        );
                      }
                      for (const action of actionList) {
                        if (ENDING.has(action.status)) continue;
                        (action.status === 'confirmed' || action.status === PRIMARY_ACTION[r.status] ? gold : quiet)
                          .push(actionButton(action));
                      }
                      if (priceAction !== undefined) {
                        /* ⭐ Board 2814 M2 (DD-11): gold only where the table's primary is Save
                           price (row 1); a New row whose date passed (row 2) is Done, and keeps
                           the control quiet. */
                        (where.primary === 'save-price' ? gold : quiet).push(
                          <button
                            key="price"
                            type="button"
                            class={
                              where.primary === 'save-price'
                                ? 'btn btn--primary btn--inline'
                                : 'btn btn--quiet btn--inline'
                            }
                            disabled={busyId === r.requestId}
                            onClick={() => void act(r, priceAction.status)}
                          >
                            {isConfirmation(r.requestId) ? 'Confirm the agreed price' : priceAction.label}
                          </button>,
                        );
                      }
                      /* ⭐ Board 2814 M2 (DD-11 row 10): a Confirmed request whose live booking
                         disagrees with it is put right ON the booking, so the row's one gold
                         primary is that navigation. */
                      if (where.row === '10' && crossing !== undefined && crossing.bookingId !== null) {
                        const bookingId = crossing.bookingId;
                        gold.push(
                          <Button key="open-booking" variant="primary" size="inline" onClick={() => onOpenBooking(bookingId)}>
                            Open the booking
                          </Button>,
                        );
                      }
                      /* ⭐⭐ Board 2814 M4 (DD-11 rows 7 and 8) - THE TWO QUIET CONTROLS that settle
                         an old unbooked row by hand, after the gold Confirm, and withdrawn while
                         the confirm step is open (one decision on screen at a time). */
                      if ((where.row === '7' || where.row === '8') && crossingId !== r.requestId) {
                        quiet.push(
                          <Button key="settle-booked" variant="quiet" size="inline" disabled={busyId === r.requestId} onClick={() => void settleRow(r, 'booked_another_way')}>
                            {SETTLE_REASON_WORDS.booked_another_way}
                          </Button>,
                          <Button key="settle-not" variant="quiet" size="inline" disabled={busyId === r.requestId} onClick={() => void settleRow(r, 'not_going_ahead')}>
                            {SETTLE_REASON_WORDS.not_going_ahead}
                          </Button>,
                        );
                      }
                      /* ⭐ Board 2814 M4 (DD-11 row 14): a row settled by hand sits in Done with
                         its words and one quiet Undo. */
                      if (where.row === '14') {
                        quiet.push(
                          <Button key="undo" variant="quiet" size="inline" disabled={busyId === r.requestId} onClick={() => void settleRow(r, null)}>
                            Undo
                          </Button>,
                        );
                      }
                      const endings = actionList.filter((a) => ENDING.has(a.status));
                      /* ⭐⭐ board 2812 M3 fix round 2 (PA-1) - THE HOME'S ASK, ON THE SAME ROW. */
                      const askEnd: VNode[] = [];
                      if (askLine !== null) {
                        const reasonPicked = (declineReason[r.requestId] ?? '') !== '';
                        if (r.askKind === 'change') {
                          // Gold on DD-11 row 9 only (Board 2814 M2), first in the start group.
                          gold.unshift(
                            <Button
                              key="ask-accept"
                              variant={where.gold && where.primary === 'accept' ? 'primary' : 'quiet'}
                              size="inline"
                              disabled={busyId === r.requestId}
                              onClick={() => void answerAsk(r, 'accept')}
                            >
                              Accept the change
                            </Button>,
                          );
                        } else {
                          // A cancel ask's accept ENDS the trip: destructive, never gold, and it
                          // confirms in place (Board 2788 M6). Board 2814 M4 (ruling (c)): a
                          // travelled run stays on the bill, so the button promises no end to it.
                          askEnd.push(
                            <Button
                              key="ask-cancel"
                              variant="destructive-quiet"
                              size="inline"
                              disabled={busyId === r.requestId}
                              onClick={() => setEndingAct({ requestId: r.requestId, status: ASK_CANCEL_ACT })}
                            >
                              {`${repeating ? 'Stop' : 'Cancel'} the trip${linkedRun?.live === true && !linkedRun.travelled ? ' and end the run' : ''}`}
                            </Button>,
                          );
                        }
                        askEnd.push(
                          <Button
                            key="ask-decline"
                            variant="destructive-quiet"
                            size="inline"
                            disabled={busyId === r.requestId || !reasonPicked}
                            ariaDescribedBy={reasonPicked ? undefined : `portal-decline-hint-${rowKey}`}
                            onClick={() => {
                              // ⛔ In the handler too, not only by `disabled` (a dispatched
                              // click walks past it): no reason, no question; answerAsk says why.
                              if (!reasonPicked) { void answerAsk(r, 'decline'); return; }
                              setEndingAct({ requestId: r.requestId, status: ASK_DECLINE_ACT, askSeq: r.askSeq ?? undefined });
                            }}
                          >
                            {r.askKind === 'change' ? CHANGE_CARD_DECLINE : 'Decline'}
                          </Button>,
                        );
                      }
                      if (gold.length === 0 && quiet.length === 0 && endings.length === 0 && askEnd.length === 0) return null;
                      return (
                        <ActionRow end={endings.length === 0 && askEnd.length === 0 ? undefined : <>{askEnd}{endings.map(actionButton)}</>}>
                          {gold}
                          {quiet}
                        </ActionRow>
                      );
                    })()}
                    </div>

                    {/* ⭐⭐ T1749 M7 wave-3 (ux BLOCK 1, impeccable P1-2, R11) —
                        THE FACTS SIT AFTER THE CONTROLS, AND THE ORDER IS A
                        MEASUREMENT RATHER THAN A PREFERENCE. Measured at 1024x700
                        with a `new` row open and scrollTop 0: the gold primary sat
                        at y 754.8, 110.8px BELOW the ~700 plane R11 pins, because
                        four facts rows stood between the pricing controls and it.
                        The facts are reference material a reader consults; the
                        price is the act she came to do, and §5.2 puts the act
                        where she can reach it.
                        ⛔ NOTHING IS HIDDEN AND NOTHING MOVED OFF THE CARD: the
                        same four facts render, under the controls. The row also
                        scrolls its handle to the top of the pane when it opens,
                        which is the other half of the fix.
                        ⛔ EXACTLY WHAT THE CLOSED ROW DOES NOT CARRY: Nurse,
                        the nurse's note, Paying, Ref, Requested. (⭐ board 2637:
                        `Pickup` left this list, because the row's lead line now
                        says it. ⭐ board 2707 M1: `Requested` joined it, because
                        DESIGN.md R4 caps a closed row at six facts and puts
                        anything else in this table; R-2 asks for it when Helen
                        VIEWS the request, which is this card.) The
                        closed row's six facts stay above,
                        so no fact is on one card twice, which is Alex's rider.
                        ⚠️ NO `Vehicle` ROW: R12 decision (ii) keeps the
                        accessible-vehicle fact beside the passenger (ADR2-D15).
                        ⚠️ A FACT IS OMITTED, NEVER PRINTED BLANK. */}
                    <FactsTable
                      facts={[
                        // ⭐ board 2812 M3 fix round 2 (UX-8): a Done row's head drops its
                        // where line, so its destination is here, behind the tap.
                        ...(where.place === 'done' && (r.destinationName ?? '') !== ''
                          ? [{ term: 'Going to', value: r.destinationName }]
                          : []),
                        // ⭐ WHO ASKED, BEHIND THE TAP. The nurse's name was a
                        // COLUMN on the ten-column table and is not one of R2's
                        // six, so it moves here with the reference and the payer
                        // rather than being dropped: Helen rings the ward by name,
                        // and a request row that could not say who asked would
                        // cost her a phone call.
                        { term: 'Nurse', value: r.nurseDisplayName },
                        // ⭐⭐ board 2637 (D4; ADR-0001 §8.3 AMENDMENT) — THE NURSE'S
                        // NOTE, a Preact text node in the facts table, which sits
                        // BELOW every action in the open row, so 500 characters
                        // cannot push the gold primary down (SC-10c).
                        // ⛔ READ HERE AND IN THE CONFIRM STEP (board 2814 M8R1, R4)
                        // AND NOWHERE ELSE: "Note for the home" is Helen's own box
                        // and goes to drivers through the booking.
                        // ⛔ Board 2647 (S1): `data-fb-redact` makes the feedback
                        // widget's PAGE PICTURE print "[hidden]" in its place,
                        // and covers nothing else: "Record screen and voice"
                        // records the real screen, and an attached picture or
                        // clip is the reviewer's own file, so either can carry
                        // the note into a bundle (ADR-0001 §8.3 AMENDMENT).
                        ...(nurseNote !== undefined
                          ? [{ term: 'Nurse’s note', value: <span class="nurse-note-value" data-fb-redact="">{nurseNote}</span> }]
                          : []),
                        { term: 'Paying', value: word(PAYER_WORDS, r.payerKind) },
                        // ⛔ THE VALUE KEEPS `.job-ref-value`, AND THE CLASS IS THE
                        // WHOLE FIX (T1683 M3 review, F1/F2): a reference must
                        // never break across lines, and `-` is a soft break
                        // opportunity in every browser while the ACCOUNT form
                        // (`KLNH 01`) breaks at its space. The rule is unchanged in
                        // `app.css`; only the element it dresses moved from a `<td>`
                        // to this fact's value.
                        // ⚠️ `?? ''` AND A LENGTH CHECK, not `!== null`: a row off
                        // the wire may carry no key at all (a pre-M3 request from
                        // an older portal), and `undefined !== null` is true — so a
                        // strict-null guard would render a labelled Ref row over
                        // nothing, which is the placeholder this rule forbids.
                        ...((r.jobRef ?? '') !== ''
                          ? [{ term: 'Ref', value: <span class="job-ref-value">{r.jobRef}</span> }]
                          : []),
                        // ⭐ board 2707 (M1, R-2) — WHEN THE HOME ASKED, in Dublin
                        // time, the LAST fact as on the shared portal card. Omitted
                        // (never blank) when the instant does not parse.
                        ...(requestedAt !== null ? [{ term: 'Requested', value: requestedAt }] : []),
                        // ⭐ Board 2788 M6 (step 2, R-6, DD-11) - WHAT WAS ASKED AND CHANGED,
                        // newest first as the portal keeps it, each line labelled by the
                        // request's own kind of time. Omitted, never blank.
                        ...(historyLines.length > 0
                          ? [{
                            term: 'History',
                            value: (
                              <ul class="portal-request-history">
                                {historyLines.map((t, i) => <li key={i}>{t}</li>)}
                              </ul>
                            ),
                          }]
                          : []),
                      ]}
                    />
                  </div>
              </Row>
              );
            };
            return (
              <>
                {inTodo !== true && <h2 class="screen-sub">Needs you now</h2>}
                {lists.needs.length > 0
                  ? <List label="Needs you now" beside={beside}>{lists.needs.map((r, i) => renderRow(r, i))}</List>
                  : <EmptyState headline="Nothing needs you now." />}
                {(() => {
                  const doneList = lists.done.length > 0
                    ? <List label="Done" beside={beside}>{lists.done.map((r, i) => renderRow(r, lists.needs.length + i))}</List>
                    : null;
                  // ⭐ board 2812 M3 fix round 2 (UX-8): a hosting To do draws Done LAST,
                  // under its own "Done" heading; on its own the list keeps it here.
                  if (arrange !== undefined) {
                    doneNode = doneList;
                    return null;
                  }
                  return doneList === null ? null : <><h2 class="screen-sub">Done</h2>{doneList}</>;
                })()}
              </>
            );
      })()}
    </>
  );
  return arrange === undefined ? body : arrange(body, doneNode);
}
